import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Spinner from './Spinner';
import DataTable, { ActionMenu } from './DataTable';
import { Icons } from './ActionIcons';
import './COwnerCockpit.css';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

interface Sistema {
  nome: string;
  descricao?: string;
  integrationId?: string;
}

interface Cliente {
  _id: string;
  nome: string;
  tiposSolicitacao?: string[];
  sistemas?: Sistema[];
}

interface Questionario {
  _id: string;
  titulo: string;
  perguntas: any[];
  pontuacaoAprovacao?: number;
}

interface Politica {
  _id: string;
  nome: string;
  descricao?: string;
  objetivos?: string;
  controles: string[];
  ativo: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ControlFinding {
  type: 'violation' | 'warning' | 'info';
  description: string;
  data: Record<string, any>;
  severity: 'critical' | 'high' | 'medium' | 'low';
}

interface ControleExecution {
  controlId: string;
  controlName: string;
  controlDescription?: string;
  controlCategory?: 'authorization' | 'access' | 'segregation' | 'audit' | 'security' | 'compliance';
  controlSeverity?: 'critical' | 'high' | 'medium' | 'low';
  controlFrameworks?: string[];
  controlTechnicalDetails?: string;
  controlRisk?: string;
  sapClientId?: string;
  executedAt?: string;
  status: 'pending' | 'running' | 'completed' | 'error';
  result?: {
    passed: boolean;
    findings: ControlFinding[];
    totalRecords: number;
    totalViolations: number;
    rawData?: any[];
    summary?: string;
    recommendations?: string[];
  };
  error?: string;
  executionTime?: number;
  systemName?: string;
  integrationId?: string;
  integrationName?: string;
}

const COwnerCockpit: React.FC = () => {
  const [theme] = useState(localStorage.getItem('phoenixTheme') || 'light');
  const [loading, setLoading] = useState(false);
  
  // Estados do modal de registro
  const [showRegistroModal, setShowRegistroModal] = useState(false);
  const [step, setStep] = useState<'dados' | 'questionario' | 'diagnostico'>('dados');
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  
  // Estados do diagnóstico
  const [pontuacaoObtida, setPontuacaoObtida] = useState(0);
  const [pontuacaoAprovacao, setPontuacaoAprovacao] = useState(0);
  const [diagnostico, setDiagnostico] = useState<'aprovado' | 'rejeitado' | 'rejeitado-automaticamente' | ''>('');
  const [recomendacoes, setRecomendacoes] = useState<string[]>([]);
  const [rejeitadoAutomaticamente, setRejeitadoAutomaticamente] = useState(false);
  const [alternativasComRejeitacao, setAlternativasComRejeitacao] = useState<Array<{ pergunta: string; alternativa: string }>>([]);
  
  // Estados para modal de confirmação de exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  
  // Estados para modal de políticas
  const [showPoliticasModal, setShowPoliticasModal] = useState(false);
  const [selectedClientePoliticas, setSelectedClientePoliticas] = useState<string>('');
  const [politicasDisponiveis, setPoliticasDisponiveis] = useState<Politica[]>([]);
  const [selectedPoliticas, setSelectedPoliticas] = useState<string[]>([]);
  const [selectedSistema, setSelectedSistema] = useState<string>('');
  const [sistemasDisponiveis, setSistemasDisponiveis] = useState<Sistema[]>([]);
  const [loadingPoliticas, setLoadingPoliticas] = useState(false);
  const [executingPoliticas, setExecutingPoliticas] = useState(false);
  const [execucaoResults, setExecucaoResults] = useState<Record<string, ControleExecution[]>>({});
  const cancelExecutionRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Estados para modal de detalhes do controle
  const [showControleDetailsModal, setShowControleDetailsModal] = useState(false);
  const [selectedControleExecution, setSelectedControleExecution] = useState<ControleExecution | null>(null);
  const [currentDetailPage, setCurrentDetailPage] = useState(1);
  const itemsPerDetailPage = 20;

  // Estados para modal de resultados históricos
  const [showResultadosModal, setShowResultadosModal] = useState(false);
  const [execucoesHistoricas, setExecucoesHistoricas] = useState<any[]>([]);
  const [loadingExecucoesHistoricas, setLoadingExecucoesHistoricas] = useState(false);
  const [selectedExecucaoHistorica, setSelectedExecucaoHistorica] = useState<any | null>(null);
  const [showExecucaoDetalhesModal, setShowExecucaoDetalhesModal] = useState(false);
  const [savingExecucao, setSavingExecucao] = useState(false);
  
  // Estados para paginação do histórico
  const [currentHistoricoPage, setCurrentHistoricoPage] = useState(1);
  const [itemsPerHistoricoPage] = useState(5);
  const [historicoSearch, setHistoricoSearch] = useState('');
  const [historicoSortField, setHistoricoSortField] = useState<string>('');
  const [historicoSortOrder, setHistoricoSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Estados para busca e ordenação na tabela de controles
  const [controleSearch, setControleSearch] = useState('');
  const [controleSortField, setControleSortField] = useState<string>('');
  const [controleSortOrder, setControleSortOrder] = useState<'asc' | 'desc'>('asc');
  const [controleCurrentPage, setControleCurrentPage] = useState(1);
  const [controleItemsPerPage] = useState(10);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Estados para jobs em background
  const [executingBackgroundJob, setExecutingBackgroundJob] = useState(false);
  const [jobsAtivos, setJobsAtivos] = useState<any[]>([]);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Estados para modal de confirmação de exclusão
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<any>(null);
  
  // Estados para modal de confirmação de cancelamento de job
  const [showCancelJobModal, setShowCancelJobModal] = useState(false);
  const [jobToCancel, setJobToCancel] = useState<string | null>(null);
  
  // Estados para toasts
  const [toasts, setToasts] = useState<Array<{id: number, message: string, type: 'success' | 'danger' | 'warning' | 'info'}>>([]);
  const [nextToastId, setNextToastId] = useState(1);
  
  // Configuração de colunas visíveis
  const [visibleColumns, setVisibleColumns] = useState({
    cliente: true,
    tipo: true,
    status: true,
    progresso: true,
    ultimaAtualizacao: true,
    acoes: true,
    createdBy: true,
    nomeSolicitante: false,
    numeroSolicitacao: true,
    usuarioId: false,
    nomeUsuario: false,
    motivoSolicitacao: false,
    funcaoSAPSolicitada: false,
    sistema: true,
    pontuacaoObtida: true,
    diagnostico: true
  });
  
  // Estados dos dados da solicitação
  const [clientesDisponiveis, setClientesDisponiveis] = useState<Cliente[]>([]);
  const [dadosSolicitacao, setDadosSolicitacao] = useState({
    clienteId: '',
    tipoSolicitacao: '',
    nomeSolicitante: '',
    numeroSolicitacao: '',
    usuarioId: '',
    nomeUsuario: '',
    motivoSolicitacao: '',
    funcaoSAPSolicitada: '',
    sistema: ''
  });
  
  // Estados do questionário
  const [questionario, setQuestionario] = useState<Questionario | null>(null);
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [respostas, setRespostas] = useState<any>({});
  const [historicoPerguntasVisitadas, setHistoricoPerguntasVisitadas] = useState<number[]>([]);
  const [tipoSolicitacaoAnterior, setTipoSolicitacaoAnterior] = useState<string>('');
  const [alerta, setAlerta] = useState<{ tipo: 'danger' | 'success' | 'warning' | 'info'; mensagem: string } | null>(null);
  
  // Estados para salvar/carregar solicitações
  const [requestId, setRequestId] = useState<string>('');
  const [savedRequests, setSavedRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  
  // Estados para pesquisa, ordenação e paginação
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
    loadSavedRequests(); // Carregar solicitações salvas ao montar
  }, [theme]);

  const loadSavedRequests = async () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-requests/my-requests` 
      : `/api/cowner-requests/my-requests`;

    try {
      setLoadingRequests(true);
      const response = await axios.get(url, { withCredentials: true });
      setSavedRequests(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar solicitações salvas:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSalvarProgressoComRespostasFiltradas = async (
    respostasFiltradas: any,
    pontuacao?: number,
    pontuacaoMin?: number, 
    diag?: string, 
    recs?: string[], 
    statusOverride?: string
  ) => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-requests/save` 
      : `/api/cowner-requests/save`;

    const clienteSelecionado = clientesDisponiveis.find(c => c._id === dadosSolicitacao.clienteId);
    
    console.log('[Frontend] Salvando progresso com respostas filtradas...');
    console.log('[Frontend] respostasFiltradas:', respostasFiltradas);
    console.log('[Frontend] dadosSolicitacao:', dadosSolicitacao);
    console.log('[Frontend] clienteSelecionado:', clienteSelecionado);
    console.log('[Frontend] requestId:', requestId);
    
    try {
      const currentStep = statusOverride || step;
      
      const payload: any = {
        clienteId: dadosSolicitacao.clienteId,
        clienteNome: clienteSelecionado?.nome || '',
        tipoSolicitacao: dadosSolicitacao.tipoSolicitacao,
        nomeSolicitante: dadosSolicitacao.nomeSolicitante,
        numeroSolicitacao: dadosSolicitacao.numeroSolicitacao,
        usuarioId: dadosSolicitacao.usuarioId,
        nomeUsuario: dadosSolicitacao.nomeUsuario,
        motivoSolicitacao: dadosSolicitacao.motivoSolicitacao,
        funcaoSAPSolicitada: dadosSolicitacao.funcaoSAPSolicitada,
        sistema: dadosSolicitacao.sistema,
        questionarioId: questionario?._id || null,
        questionarioTitulo: questionario?.titulo || '',
        respostas: respostasFiltradas, // Usar respostas filtradas
        perguntaAtual: perguntaAtual,
        status: currentStep,
        percentualConclusao: (() => {
          if (currentStep === 'dados') return 0;
          if (currentStep === 'diagnostico' || currentStep === 'completo') return 100;
          if (!questionario) return 0;
          const totalPerguntas = questionario.perguntas.length;
          const perguntasRespondidas = questionario.perguntas.filter((p, index) => {
            const status = getStatusPergunta(index);
            return status === 'respondida' || status === 'pulada';
          }).length;
          return Math.round((perguntasRespondidas / totalPerguntas) * 100);
        })()
      };
      
      // Adicionar campos de diagnóstico se fornecidos
      if (pontuacao !== undefined) payload.pontuacaoObtida = pontuacao;
      if (pontuacaoMin !== undefined) payload.pontuacaoAprovacao = pontuacaoMin;
      if (diag) payload.diagnostico = diag;
      if (recs && recs.length > 0) payload.recomendacoes = recs;

      // Só adiciona _id se existir
      if (requestId) {
        payload._id = requestId;
      }

      console.log('[Frontend] Payload a ser enviado:', JSON.stringify(payload, null, 2));
      console.log('[Frontend] URL:', url);
      console.log('[Frontend] Status sendo salvo:', payload.status);
      console.log('[Frontend] Diagnóstico sendo salvo:', payload.diagnostico);

      const response = await axios.post(url, payload, { withCredentials: true });
      console.log('[Frontend] Resposta recebida:', response.data);
      console.log('[Frontend] Status retornado:', response.data.status);
      console.log('[Frontend] Diagnóstico retornado:', response.data.diagnostico);
      
      setRequestId(response.data._id);
      setAlerta({ tipo: 'success', mensagem: 'Progresso salvo com sucesso!' });
      setTimeout(() => setAlerta(null), 3000);
      await loadSavedRequests(); // Recarregar lista
    } catch (err: any) {
      console.error('[Frontend] Erro ao salvar progresso:', err);
      console.error('[Frontend] Resposta do erro:', err.response?.data);
      setAlerta({ tipo: 'danger', mensagem: err.response?.data?.message || 'Erro ao salvar progresso' });
      setTimeout(() => setAlerta(null), 5000);
    }
  };

  // Função wrapper que usa as respostas do estado atual
  const handleSalvarProgresso = async (
    pontuacao?: number,
    pontuacaoMin?: number, 
    diag?: string, 
    recs?: string[], 
    statusOverride?: string
  ) => {
    await handleSalvarProgressoComRespostasFiltradas(
      respostas,
      pontuacao,
      pontuacaoMin,
      diag,
      recs,
      statusOverride
    );
  };

  const handleCarregarRequest = async (id: string) => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-requests/${id}` 
      : `/api/cowner-requests/${id}`;

    try {
      const response = await axios.get(url, { withCredentials: true });
      const data = response.data;
      
      // Carregar dados
      setRequestId(data._id);
      setDadosSolicitacao({
        clienteId: data.clienteId,
        tipoSolicitacao: data.tipoSolicitacao,
        nomeSolicitante: data.nomeSolicitante || '',
        numeroSolicitacao: data.numeroSolicitacao || '',
        usuarioId: data.usuarioId || '',
        nomeUsuario: data.nomeUsuario || '',
        motivoSolicitacao: data.motivoSolicitacao || '',
        funcaoSAPSolicitada: data.funcaoSAPSolicitada || '',
        sistema: data.sistema || ''
      });
      
      // Carregar respostas - SEMPRE carregar, independente do status
      const respostasCarregadas = data.respostas || {};
      console.log('[Carregamento] Respostas do banco:', respostasCarregadas);
      
      // Se tem questionário salvo, carregar e filtrar
      if (data.questionarioId) {
        // Carregar questionário
        const qUrl = baseNoSlash 
          ? `${baseNoSlash}/api/cowner-questionarios/${data.questionarioId}` 
          : `/api/cowner-questionarios/${data.questionarioId}`;
        const qResponse = await axios.get(qUrl, { withCredentials: true });
        setQuestionario(qResponse.data);
        setPerguntaAtual(data.perguntaAtual || 0);
        
        // Filtrar respostas para manter apenas as alcançáveis
        const perguntasAlcancaveisSet = new Set<string>();
        const visitados = new Set<number>();
        const fila: number[] = [0];
        
        while (fila.length > 0) {
          const indice = fila.shift()!;
          
          if (visitados.has(indice) || indice < 0 || indice >= qResponse.data.perguntas.length) {
            continue;
          }
          
          visitados.add(indice);
          const pergunta = qResponse.data.perguntas[indice];
          perguntasAlcancaveisSet.add(pergunta.id);
          
          const resposta = respostasCarregadas[pergunta.id];
          let proximoIndex = -1;
          
          if (pergunta.tipo === 'unica-escolha' && resposta) {
            const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(resposta));
            if (alternativaSelecionada?.proximaPergunta && 
                alternativaSelecionada.proximaPergunta !== 'FIM' && 
                alternativaSelecionada.proximaPergunta !== '') {
              proximoIndex = qResponse.data.perguntas.findIndex((p: any) => p.id === alternativaSelecionada.proximaPergunta);
            }
          } else if (pergunta.tipo === 'multipla-escolha' && Array.isArray(resposta) && resposta.length > 0) {
            for (const valor of resposta) {
              const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(valor));
              if (alternativaSelecionada?.proximaPergunta && 
                  alternativaSelecionada.proximaPergunta !== 'FIM' && 
                  alternativaSelecionada.proximaPergunta !== '') {
                proximoIndex = qResponse.data.perguntas.findIndex((p: any) => p.id === alternativaSelecionada.proximaPergunta);
                if (proximoIndex !== -1) break;
              }
            }
          }
          
          if (proximoIndex === -1 && indice < qResponse.data.perguntas.length - 1) {
            proximoIndex = indice + 1;
          }
          
          if (proximoIndex !== -1) {
            fila.push(proximoIndex);
          }
        }
        
        // Filtrar respostas mantendo apenas as alcançáveis
        const respostasFiltradas: any = {};
        Object.keys(respostasCarregadas).forEach(key => {
          if (perguntasAlcancaveisSet.has(key)) {
            respostasFiltradas[key] = respostasCarregadas[key];
          } else {
            console.log(`[Carregamento] Descartando resposta não alcançável: ${key}`);
          }
        });
        
        setRespostas(respostasFiltradas);
        console.log('[Carregamento] Respostas filtradas carregadas:', respostasFiltradas);
        
        // Reconstruir histórico de perguntas visitadas com base nas respostas filtradas
        const perguntasRespondidas: number[] = [];
        qResponse.data.perguntas.forEach((pergunta: any, index: number) => {
          if (respostasFiltradas[pergunta.id]) {
            perguntasRespondidas.push(index);
          }
        });
        
        // Se tem perguntas respondidas, adicionar ao histórico
        if (perguntasRespondidas.length > 0) {
          setHistoricoPerguntasVisitadas(perguntasRespondidas);
        }
        
        // Definir step baseado no status
        if (data.status === 'diagnostico') {
          // Recalcular o diagnóstico sempre que carregar uma solicitação finalizada
          // Isso garante que o status de rejeição automática seja atualizado mesmo após F5
          const resultadoDiagnostico = calcularDiagnostico(qResponse.data, respostasFiltradas);
          
          setPontuacaoObtida(resultadoDiagnostico.pontuacao);
          setPontuacaoAprovacao(resultadoDiagnostico.pontuacaoMinima);
          setDiagnostico(resultadoDiagnostico.resultadoDiagnostico as 'aprovado' | 'rejeitado' | 'rejeitado-automaticamente');
          setRecomendacoes(resultadoDiagnostico.recomendacoesEncontradas);
          setRejeitadoAutomaticamente(resultadoDiagnostico.temRejeitarAutomaticamente);
          setAlternativasComRejeitacao(resultadoDiagnostico.alternativasRejeitadas);
          
          setStep('diagnostico');
        } else if (data.status === 'questionario') {
          setStep('questionario');
        } else {
          // Status 'dados' mas tem questionário e respostas - manter no step dados mas com respostas carregadas
          setStep('dados');
        }
      } else {
        // Se não tem questionário, apenas carregar as respostas diretas (se houver)
        setRespostas(respostasCarregadas);
        console.log('[Carregamento] Sem questionário, respostas carregadas:', respostasCarregadas);
        setStep('dados');
      }
      
      await loadClientesDisponiveis();
      setShowRegistroModal(true);
      setIsViewMode(false);
      setAlerta({ tipo: 'info', mensagem: 'Solicitação carregada com sucesso!' });
      setTimeout(() => setAlerta(null), 3000);
    } catch (err: any) {
      console.error('Erro ao carregar solicitação:', err);
      setAlerta({ tipo: 'danger', mensagem: 'Erro ao carregar solicitação' });
      setTimeout(() => setAlerta(null), 3000);
    }
  };

  const handleVisualizarRequest = async (id: string) => {
    await handleCarregarRequest(id);
    setIsViewMode(true);
    setStep('dados'); // Sempre começa mostrando os dados
  };

  const handleExportarPDF = () => {
    // Helper para normalizar e evitar caracteres estranhos (acentos, NBSP etc.)
    const sanitize = (s: any) => (s ?? '')
      .toString()
      .normalize('NFC')
      .replace(/\u00A0/g, ' ');

    const doc = new jsPDF();
    // Usar fonte padrão 'times' que tem melhor compatibilidade com acentuação nos built-ins do jsPDF
    doc.setFont('helvetica', 'normal');
    const pageWidth = doc.internal.pageSize.width;
    let yPos = 20;

    // Título do documento
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Relatório de Solicitação - Compliance Owner', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;

    // Linha divisória
    doc.setLineWidth(0.5);
    doc.line(20, yPos, pageWidth - 20, yPos);
    yPos += 8;

    // Seção: Dados da Solicitação
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
    doc.text('Dados da Solicitação', 20, yPos);
    yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');

  const clienteNome = sanitize(clientesDisponiveis.find(c => c._id === dadosSolicitacao.clienteId)?.nome || '-');
    
    const dadosTable = [
      ['Cliente:', clienteNome],
      ['Tipo de Solicitação:', sanitize(dadosSolicitacao.tipoSolicitacao || '-')],
      ['Nome do Solicitante:', sanitize(dadosSolicitacao.nomeSolicitante || '-')],
      ['Nº da Solicitação:', sanitize(dadosSolicitacao.numeroSolicitacao || '-')],
      ['Usuário (ID):', sanitize(dadosSolicitacao.usuarioId || '-')],
      ['Nome do Usuário:', sanitize(dadosSolicitacao.nomeUsuario || '-')],
      ['Função SAP Solicitada:', sanitize(dadosSolicitacao.funcaoSAPSolicitada || '-')],
      ['Sistema:', sanitize(dadosSolicitacao.sistema || '-')],
      ['Motivo da Solicitação:', sanitize(dadosSolicitacao.motivoSolicitacao || '-')]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [],
      body: dadosTable,
      theme: 'plain',
      styles: { 
        fontSize: 9,
        cellPadding: 3,
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        cellWidth: 'wrap',
        overflow: 'linebreak',
        font: 'helvetica'
      },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 110 }
      },
      margin: { left: 20, right: 20 }
    });

    yPos = (doc as any).lastAutoTable.finalY + 15;

    // Verificar se há diagnóstico
    if (diagnostico) {
      // Nova página se necessário
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }

      // Seção: Diagnóstico
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
      doc.text('Diagnóstico', 20, yPos);
      yPos += 8;

      // Análise de Pontuação
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
      doc.text('Análise de Pontuação', 20, yPos);
      yPos += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(sanitize(`Pontuação Obtida: ${pontuacaoObtida} pontos`), 25, yPos);
      yPos += 5;
  doc.text(sanitize(`Pontuação Mínima para Aprovação: ${pontuacaoAprovacao} pontos`), 25, yPos);
      yPos += 10;

      // Resultado do Diagnóstico
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      const diagnosticoTexto = diagnostico === 'aprovado' ? 'APROVAR' : 'REJEITAR';
      doc.text(sanitize(`Recomendação: ${diagnosticoTexto}`), 20, yPos);
      yPos += 6;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const diagnosticoDescricao = diagnostico === 'aprovado' 
        ? 'A solicitação atingiu a pontuação mínima necessária e pode ser aprovada.'
        : diagnostico === 'rejeitado-automaticamente'
          ? 'A solicitação foi REJEITADA AUTOMATICAMENTE devido a uma ou mais respostas marcadas como críticas.'
          : 'A solicitação não atingiu a pontuação mínima necessária. Recomenda-se rejeitar ou solicitar ajustes.';
      
      const splitText = doc.splitTextToSize(sanitize(diagnosticoDescricao), pageWidth - 50);
      doc.text(splitText, 25, yPos);
      yPos += splitText.length * 5 + 8;

      // Alerta de Rejeição Automática
      if (rejeitadoAutomaticamente && alternativasComRejeitacao.length > 0) {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(sanitize('REJEIÇÃO AUTOMÁTICA ACIONADA'), 20, yPos);
        yPos += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(sanitize('Respostas críticas identificadas:'), 25, yPos);
        yPos += 6;

        alternativasComRejeitacao.forEach((item) => {
          if (yPos > 260) {
            doc.addPage();
            yPos = 20;
          }
          
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          const perguntaSplit = doc.splitTextToSize(sanitize(`• Pergunta: ${item.pergunta}`), pageWidth - 60);
          doc.text(perguntaSplit, 30, yPos);
          yPos += perguntaSplit.length * 5;
          
          doc.setFont('helvetica', 'normal');
          const respostaSplit = doc.splitTextToSize(sanitize(`  Resposta: ${item.alternativa}`), pageWidth - 60);
          doc.text(respostaSplit, 30, yPos);
          yPos += respostaSplit.length * 5 + 3;
        });
        yPos += 5;
      }

      // Recomendações
      if (recomendacoes.length > 0) {
        if (yPos > 240) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(sanitize('Recomendações e Pontos de Atenção:'), 20, yPos);
        yPos += 6;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        recomendacoes.forEach((rec) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          const recSplit = doc.splitTextToSize(sanitize(`• ${rec}`), pageWidth - 50);
          doc.text(recSplit, 25, yPos);
          yPos += recSplit.length * 5 + 2;
        });
        yPos += 8;
      }

      // Resumo das Respostas
      if (questionario && Object.keys(respostas).length > 0) {
        if (yPos > 230) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(sanitize('Resumo das Respostas'), 20, yPos);
        yPos += 8;

        const respostasData: any[] = [];
        questionario.perguntas.forEach((pergunta: any) => {
          const resposta = respostas[pergunta.id];
          if (!resposta) return;

          let respostaTexto = '';
          if (Array.isArray(resposta)) {
            respostaTexto = resposta.map(r => {
              const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(r));
              return alt?.texto || r;
            }).join(', ');
          } else {
            const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(resposta));
            respostaTexto = alt?.texto || resposta;
          }

          const temRejeitacao = alternativasComRejeitacao.some(
            item => item.pergunta === pergunta.titulo && item.alternativa === respostaTexto
          );

          respostasData.push([
            sanitize(pergunta.titulo + (temRejeitacao ? ' [CRÍTICA]' : '')),
            sanitize(respostaTexto)
          ]);
        });

        autoTable(doc, {
          startY: yPos,
          head: [['Pergunta', 'Resposta']],
          body: respostasData,
          theme: 'striped',
          styles: { 
            fontSize: 8,
            cellPadding: 3,
            cellWidth: 'wrap',
            overflow: 'linebreak',
            halign: 'left',
            valign: 'top',
            font: 'helvetica'
          },
          headStyles: {
            fillColor: [80, 80, 80],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'left',
            font: 'helvetica'
          },
          columnStyles: {
            0: { cellWidth: 70, fontStyle: 'bold' },
            1: { cellWidth: 100 }
          },
          margin: { left: 20, right: 20 }
        });
      }
    }

    // Rodapé com data de geração
    const totalPages = (doc as any).internal.getNumberOfPages();
    const dataGeracao = new Date().toLocaleString('pt-BR');
    
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `Gerado em: ${dataGeracao} | Página ${i} de ${totalPages}`,
        pageWidth / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
    }

    // Salvar o PDF
    const nomeArquivo = `Solicitacao_${dadosSolicitacao.numeroSolicitacao || 'sem-numero'}_${new Date().getTime()}.pdf`;
    doc.save(nomeArquivo);
  };

  const handleDeletarRequest = async (id: string) => {
    setRequestToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDeleteRequest = async () => {
    if (!requestToDelete) return;
    
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-requests/${requestToDelete}` 
      : `/api/cowner-requests/${requestToDelete}`;

    try {
      await axios.delete(url, { withCredentials: true });
      setAlerta({ tipo: 'success', mensagem: 'Solicitação deletada com sucesso!' });
      setTimeout(() => setAlerta(null), 3000);
      await loadSavedRequests();
      setShowDeleteModal(false);
      setRequestToDelete(null);
    } catch (err: any) {
      console.error('Erro ao deletar solicitação:', err);
      setAlerta({ tipo: 'danger', mensagem: 'Erro ao deletar solicitação' });
      setTimeout(() => setAlerta(null), 3000);
      setShowDeleteModal(false);
      setRequestToDelete(null);
    }
  };

  const loadClientesDisponiveis = async () => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-userxcliente/my-clientes` 
      : `/api/cowner-userxcliente/my-clientes`;

    try {
      const response = await axios.get(url, { withCredentials: true });
      setClientesDisponiveis(response.data);
    } catch (err: any) {
      console.error('Erro ao carregar clientes:', err);
      alert('Erro ao carregar clientes disponíveis');
    }
  };

  const loadPoliticasDoCliente = async (clienteId: string) => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner/cliente/${clienteId}/politicas` 
      : `/api/cowner/cliente/${clienteId}/politicas`;

    try {
      setLoadingPoliticas(true);
      const response = await axios.get(url, { withCredentials: true });
      const politicasAtivas = response.data.politicas.filter((p: Politica) => p.ativo);
      setPoliticasDisponiveis(politicasAtivas);
    } catch (err: any) {
      console.error('Erro ao carregar políticas:', err);
      setAlerta({ tipo: 'danger', mensagem: 'Erro ao carregar políticas do cliente' });
      setTimeout(() => setAlerta(null), 3000);
      setPoliticasDisponiveis([]);
    } finally {
      setLoadingPoliticas(false);
    }
  };

  const handleClientePoliticasChange = (clienteId: string) => {
    setSelectedClientePoliticas(clienteId);
    setSelectedPoliticas([]);
    setExecucaoResults({});
    setSelectedSistema('');
    setSistemasDisponiveis([]);
    
    if (clienteId) {
      loadPoliticasDoCliente(clienteId);
      
      // Carregar sistemas do cliente
      const cliente = clientesDisponiveis.find(c => c._id === clienteId);
      if (cliente && cliente.sistemas) {
        setSistemasDisponiveis(cliente.sistemas);
      }
    } else {
      setPoliticasDisponiveis([]);
    }
  };

  const handleExecutarPoliticas = async () => {
    if (selectedPoliticas.length === 0) {
      setAlerta({ tipo: 'warning', mensagem: 'Selecione ao menos uma política para executar' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    if (!selectedSistema) {
      setAlerta({ tipo: 'warning', mensagem: 'Selecione um sistema para executar os controles' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    const cliente = clientesDisponiveis.find(c => c._id === selectedClientePoliticas);
    if (!cliente) {
      setAlerta({ tipo: 'danger', mensagem: 'Cliente não encontrado' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    // Usar o sistema selecionado
    const sistemaSAP = sistemasDisponiveis.find(s => s.nome === selectedSistema);
    if (!sistemaSAP?.integrationId) {
      setAlerta({ tipo: 'danger', mensagem: 'Sistema selecionado não possui conector (integrationId) configurado' });
      setTimeout(() => setAlerta(null), 5000);
      return;
    }

    setExecutingPoliticas(true);
    cancelExecutionRef.current = false;
    abortControllerRef.current = new AbortController();
    setAlerta({ tipo: 'info', mensagem: `Iniciando execução de ${selectedPoliticas.length} política(s)...` });

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const results: Record<string, ControleExecution[]> = {};

    for (const politicaId of selectedPoliticas) {
      if (cancelExecutionRef.current) {
        setAlerta({ tipo: 'warning', mensagem: 'Execução interrompida pelo usuário' });
        setTimeout(() => setAlerta(null), 3000);
        break;
      }

      const politica = politicasDisponiveis.find(p => p._id === politicaId);
      if (!politica) continue;

      results[politicaId] = [];

      // Ordenar controles por ID em ordem crescente
      const controlesSorted = [...politica.controles].sort((a, b) => {
        // Extrai números dos IDs para ordenação natural (ex: SAP_001, SAP_002, SAP_010)
        const numA = parseInt(a.replace(/\D/g, '')) || 0;
        const numB = parseInt(b.replace(/\D/g, '')) || 0;
        return numA - numB;
      });

      for (const controleId of controlesSorted) {
        if (cancelExecutionRef.current) {
          setAlerta({ tipo: 'warning', mensagem: 'Execução interrompida pelo usuário' });
          setTimeout(() => setAlerta(null), 3000);
          break;
        }
        // Buscar dados do controle primeiro
        const controleUrl = baseNoSlash 
          ? `${baseNoSlash}/api/sap-controls/${controleId}` 
          : `/api/sap-controls/${controleId}`;
        
        let controlData: any = null;
        try {
          const controleResponse = await axios.get(controleUrl, { 
            withCredentials: true,
            signal: abortControllerRef.current?.signal
          });
          // A API retorna os dados dentro de control
          controlData = controleResponse.data.control;
          console.log(`Dados do controle ${controleId}:`, controlData);
        } catch (err: any) {
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            console.log(`Busca de dados do controle ${controleId} cancelada`);
          } else {
            console.error(`Erro ao buscar dados do controle ${controleId}:`, err);
          }
        }

        const execution: ControleExecution = {
          controlId: controleId,
          controlName: controlData?.name || controleId,
          controlDescription: controlData?.description,
          controlCategory: controlData?.category,
          controlSeverity: controlData?.severity,
          controlFrameworks: controlData?.frameworks,
          controlTechnicalDetails: controlData?.technicalDetails,
          controlRisk: controlData?.risk,
          status: 'running',
          sapClientId: sistemaSAP.integrationId,
          systemName: sistemaSAP.nome,
          integrationId: sistemaSAP.integrationId
        };
        
        console.log(`Execution object criado:`, execution);
        
        results[politicaId].push(execution);
        setExecucaoResults({...results});
        
        // Buscar nome do conector (integração) se existir integrationId
        let integrationName = '';
        if (sistemaSAP.integrationId) {
          try {
            const integrationUrl = baseNoSlash 
              ? `${baseNoSlash}/api/integrations/${sistemaSAP.integrationId}` 
              : `/api/integrations/${sistemaSAP.integrationId}`;
            const integrationResponse = await axios.get(integrationUrl, { 
              withCredentials: true,
              signal: abortControllerRef.current?.signal
            });
            integrationName = integrationResponse.data?.integration?.nome || integrationResponse.data?.nome || '';
            
            // Atualizar com o nome da integração
            results[politicaId] = results[politicaId].map(exec => 
              exec.controlId === controleId 
                ? { ...exec, integrationName }
                : exec
            );
            setExecucaoResults({ ...results });
          } catch (err: any) {
            if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
              console.log('Busca de integração cancelada');
            } else {
              console.warn('Erro ao buscar nome da integração:', err);
            }
          }
        }

        // Chamar API real de execução de controle
        const url = baseNoSlash 
          ? `${baseNoSlash}/api/sap-controls/${controleId}/execute` 
          : `/api/sap-controls/${controleId}/execute`;

        try {
          const startTime = Date.now();
          const response = await axios.post(url, { 
            sapClientId: sistemaSAP.integrationId 
          }, { 
            withCredentials: true,
            signal: abortControllerRef.current?.signal
          });
          
          const endTime = Date.now();
          const executionData = response.data.execution;

          // Criar novo objeto preservando os dados do controle
          const idx = results[politicaId].indexOf(execution);
          results[politicaId][idx] = {
            ...execution,
            status: 'completed',
            executedAt: executionData.executedAt,
            executionTime: executionData.executionTime || (endTime - startTime),
            result: executionData.result
          };
          
          console.log(`Execution após completar:`, results[politicaId][idx]);
          
        } catch (err: any) {
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
            console.log(`Execução do controle ${controleId} cancelada`);
            const idx = results[politicaId].indexOf(execution);
            results[politicaId][idx] = {
              ...execution,
              status: 'error',
              error: 'Execução cancelada pelo usuário'
            };
          } else {
            console.error(`Erro ao executar controle ${controleId}:`, err);
            const idx = results[politicaId].indexOf(execution);
            results[politicaId][idx] = {
              ...execution,
              status: 'error',
              error: err?.response?.data?.error || err.message || 'Erro desconhecido'
            };
          }
        }

        setExecucaoResults({...results});
      }
      
      if (cancelExecutionRef.current) break;
    }

    setExecutingPoliticas(false);
    abortControllerRef.current = null;
    
    if (!cancelExecutionRef.current) {
      setAlerta({ tipo: 'success', mensagem: 'Execução das políticas concluída!' });
      setTimeout(() => setAlerta(null), 5000);
    }
  };

  const handleExecutarBackground = async () => {
    if (selectedPoliticas.length === 0) {
      setAlerta({ tipo: 'warning', mensagem: 'Selecione ao menos uma política para executar' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    if (!selectedSistema) {
      setAlerta({ tipo: 'warning', mensagem: 'Selecione um sistema para executar os controles' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    const cliente = clientesDisponiveis.find(c => c._id === selectedClientePoliticas);
    if (!cliente) {
      setAlerta({ tipo: 'danger', mensagem: 'Cliente não encontrado' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    const sistemaSAP = sistemasDisponiveis.find(s => s.nome === selectedSistema);
    if (!sistemaSAP?.integrationId) {
      setAlerta({ tipo: 'danger', mensagem: 'Sistema selecionado não possui conector configurado' });
      setTimeout(() => setAlerta(null), 5000);
      return;
    }

    setExecutingBackgroundJob(true);
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';

    try {
      // Criar um job para cada política selecionada
      const jobPromises = selectedPoliticas.map(async (politicaId) => {
        const url = baseNoSlash 
          ? `${baseNoSlash}/api/cowner/cliente/${selectedClientePoliticas}/jobs`
          : `/api/cowner/cliente/${selectedClientePoliticas}/jobs`;

        return axios.post(url, {
          politicaId,
          sistemaIntegracaoId: sistemaSAP.integrationId
        }, { withCredentials: true });
      });

      await Promise.all(jobPromises);
      
      showToast(`${selectedPoliticas.length} job(s) planejado(s) com sucesso! Acompanhe o progresso em "Monitorar Execuções".`, 'success');

      // Limpar seleção
      setSelectedPoliticas([]);
      
      // Iniciar polling para atualizar jobs ativos
      startJobPolling();

    } catch (error: any) {
      console.error('Erro ao agendar jobs:', error);
      setAlerta({ 
        tipo: 'danger', 
        mensagem: `Erro ao agendar jobs: ${error?.response?.data?.error || error.message}` 
      });
      setTimeout(() => setAlerta(null), 5000);
    } finally {
      setExecutingBackgroundJob(false);
    }
  };

  // Função para iniciar polling de jobs
  const startJobPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      if (selectedClientePoliticas) {
        await loadJobsAtivos();
      }
    }, 5000); // Atualizar a cada 5 segundos

    setPollingInterval(interval);
  };

  // Função para carregar jobs ativos
  const loadJobsAtivos = async () => {
    if (!selectedClientePoliticas) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';

    try {
      const url = baseNoSlash 
        ? `${baseNoSlash}/api/cowner/cliente/${selectedClientePoliticas}/jobs?status=pending&status=running`
        : `/api/cowner/cliente/${selectedClientePoliticas}/jobs?status=pending&status=running`;

      const response = await axios.get(url, { withCredentials: true });
      const jobs = response.data.jobs || [];
      
      // Verificar se algum job foi concluído desde a última verificação
      jobsAtivos.forEach(oldJob => {
        if (oldJob.status === 'running' || oldJob.status === 'pending') {
          const updatedJob = jobs.find((j: any) => j._id === oldJob._id);
          if (!updatedJob) {
            // Job não está mais na lista de ativos, foi concluído
            showJobCompletionToast(oldJob);
          }
        }
      });

      setJobsAtivos(jobs);
    } catch (error) {
      console.error('Erro ao carregar jobs ativos:', error);
    }
  };

  // Função para exibir toast de conclusão
  const showJobCompletionToast = (job: any) => {
    showToast(`Job "${job.politicaNome}" concluído! Confira os resultados em "Monitorar Execuções".`, 'success');
  };
  
  // Função para exibir toast genérico
  const showToast = (message: string, type: 'success' | 'danger' | 'warning' | 'info' = 'success') => {
    const id = nextToastId;
    setNextToastId(id + 1);
    setToasts(prev => [...prev, { id, message, type }]);
    
    // Auto-remover após 5 segundos
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  };

  // Função para abrir modal de cancelamento de job
  const handleCancelJob = (jobId: string) => {
    setJobToCancel(jobId);
    setShowCancelJobModal(true);
  };
  
  // Função para confirmar cancelamento de job
  const confirmCancelJob = async () => {
    if (!jobToCancel) return;

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';

    try {
      const cancelUrl = baseNoSlash 
        ? `${baseNoSlash}/api/cowner/jobs/${jobToCancel}/cancel` 
        : `/api/cowner/jobs/${jobToCancel}/cancel`;

      await axios.patch(cancelUrl, {}, { withCredentials: true });

      showToast('Job cancelado com sucesso!', 'success');

      // Recarregar lista
      if (selectedClientePoliticas) {
        loadExecucoesHistoricas(selectedClientePoliticas);
      }
      
      setShowCancelJobModal(false);
      setJobToCancel(null);
    } catch (error: any) {
      console.error('Erro ao cancelar job:', error);
      showToast(error.response?.data?.error || 'Erro ao cancelar job', 'danger');
    }
  };

  // Função para abrir modal de confirmação de exclusão
  const handleDeleteRecord = (record: any) => {
    setRecordToDelete(record);
    setShowDeleteConfirmModal(true);
  };
  
  // Função para confirmar e executar exclusão
  const confirmDeleteRecord = async () => {
    if (!recordToDelete) return;
    
    const isJob = recordToDelete.isJob;
    const itemType = isJob ? 'job' : 'execução';

    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';

    try {
      const deleteUrl = isJob
        ? (baseNoSlash ? `${baseNoSlash}/api/cowner/jobs/${recordToDelete._id}` : `/api/cowner/jobs/${recordToDelete._id}`)
        : (baseNoSlash ? `${baseNoSlash}/api/cowner/execucoes/${recordToDelete._id}` : `/api/cowner/execucoes/${recordToDelete._id}`);

      await axios.delete(deleteUrl, { withCredentials: true });

      showToast(`${itemType.charAt(0).toUpperCase() + itemType.slice(1)} excluída com sucesso!`, 'success');

      // Recarregar lista
      if (selectedClientePoliticas) {
        loadExecucoesHistoricas(selectedClientePoliticas);
      }
      
      setShowDeleteConfirmModal(false);
      setRecordToDelete(null);
    } catch (error: any) {
      console.error(`Erro ao deletar ${itemType}:`, error);
      showToast(error.response?.data?.error || `Erro ao deletar ${itemType}`, 'danger');
    }
  };

  // Limpar polling ao desmontar componente
  React.useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleViewControleDetails = (execution: ControleExecution) => {
    console.log('Abrindo detalhes do controle:', execution);
    
    // Garantir que o objeto execution está completo
    if (!execution || !execution.result) {
      console.error('Execução inválida ou sem resultado:', execution);
      setAlerta({ tipo: 'warning', mensagem: 'Não há detalhes disponíveis para este controle' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }
    
    setSelectedControleExecution(execution);
    setCurrentDetailPage(1);
    setShowControleDetailsModal(true);
  };

  const loadExecucoesHistoricas = async (clienteId: string) => {
    setLoadingExecucoesHistoricas(true);
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';

    try {
      // Buscar execuções completas
      const execucoesUrl = baseNoSlash 
        ? `${baseNoSlash}/api/cowner/cliente/${clienteId}/execucoes` 
        : `/api/cowner/cliente/${clienteId}/execucoes`;
      
      const execucoesResponse = await axios.get(execucoesUrl, { 
        withCredentials: true,
        params: { limit: 100 }
      });

      // Buscar jobs (incluindo em execução)
      const jobsUrl = baseNoSlash 
        ? `${baseNoSlash}/api/cowner/cliente/${clienteId}/jobs` 
        : `/api/cowner/cliente/${clienteId}/jobs`;
      
      const jobsResponse = await axios.get(jobsUrl, { 
        withCredentials: true,
        params: { limit: 100 }
      });

      const execucoes = execucoesResponse.data.execucoes || [];
      const jobs = jobsResponse.data.jobs || [];

      // Log para debug
      console.log('=== DEBUG EXECUÇÕES E JOBS ===');
      console.log('Total de jobs:', jobs.length);
      console.log('Total de execuções:', execucoes.length);
      if (jobs.length > 0) {
        console.log('Primeiro job:', jobs[0]);
        console.log('criadoPor do job:', jobs[0].criadoPor);
      }
      if (execucoes.length > 0) {
        console.log('Primeira execução:', execucoes[0]);
        console.log('executadoPor da execução:', execucoes[0].executadoPor);
      }

      // Combinar execuções e jobs
      // Filtrar jobs que já têm execução (para não duplicar)
      const jobsParaMostrar = jobs.filter((j: any) => {
        // Se job está concluído e tem execucaoId, não mostra o job (mostra só a execução)
        if (j.status === 'completed' && j.execucaoId) {
          return false;
        }
        return true;
      });

      // Log para debug
      console.log('Jobs recebidos:', jobs.length > 0 ? jobs[0] : 'nenhum job');
      console.log('Execuções recebidas:', execucoes.length > 0 ? execucoes[0] : 'nenhuma execução');

      const combinedList = [
        ...execucoes.map((e: any) => ({ ...e, tipo: e.tipo || 'adhoc', isJob: false })),
        ...jobsParaMostrar.map((j: any) => ({
          _id: j._id,
          politicaNome: j.politicaNome,
          sistemaNome: j.sistemaNome, // Já vem preenchido do backend
          executadoEm: j.criadoEm,
          executadoPor: j.criadoPor, // criadoPor já vem populado do backend com username e fullName
          tipo: 'job',
          isJob: true,
          jobStatus: j.status,
          progresso: j.progresso,
          resumo: {
            totalControles: j.totalControles,
            controlesExecutados: j.controlesExecutados,
            controlesFalhados: j.controlesFalhados,
            totalViolacoes: 0,
            passou: false
          },
          execucaoId: j.execucaoId
        }))
      ].sort((a, b) => new Date(b.executadoEm).getTime() - new Date(a.executadoEm).getTime());

      setExecucoesHistoricas(combinedList);
      setCurrentHistoricoPage(1); // Reset to first page when data changes
    } catch (error) {
      console.error('Erro ao carregar execuções históricas:', error);
      setAlerta({ tipo: 'danger', mensagem: 'Erro ao carregar histórico de execuções' });
      setTimeout(() => setAlerta(null), 3000);
    } finally {
      setLoadingExecucoesHistoricas(false);
    }
  };

  const handleExportExecutive = async () => {
    if (!selectedExecucaoHistorica) {
      showToast('Nenhuma execução selecionada', 'danger');
      return;
    }

    const sanitize = (s: any) => (s ?? '')
      .toString()
      .normalize('NFC')
      .replace(/\u00A0/g, ' ');

    const traduzirSeveridade = (sev: string) => {
      const traducoes: Record<string, string> = {
        'critical': 'Crítica',
        'high': 'Alta',
        'medium': 'Média',
        'low': 'Baixa'
      };
      return traducoes[sev.toLowerCase()] || sev;
    };

    const doc = new jsPDF();
    doc.setFont('helvetica', 'normal');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 20;

    // Carregar logo
    const logoUrl = '/assets/img/icons/logo2.png';
    let logoData: string | null = null;
    
    try {
      // Carregar imagem como base64
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            logoData = canvas.toDataURL('image/png');
          }
          resolve();
        };
        img.onerror = reject;
        img.src = logoUrl;
      });
    } catch (error) {
      console.warn('Não foi possível carregar o logo:', error);
    }

    // ===== CAPA =====
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Visão Executiva', pageWidth / 2, pageHeight / 2 - 30, { align: 'center' });
    
    // Buscar nome do cliente
    const nomeCliente = selectedExecucaoHistorica.clienteNome || 
                        clientesDisponiveis.find((c: any) => c._id === selectedExecucaoHistorica.clienteId)?.nome ||
                        'Cliente';
    const nomePolitica = selectedExecucaoHistorica.politicaNome || 'Política';
    const tituloCompleto = `${nomeCliente} - ${nomePolitica}`;
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    // Quebrar título se for muito longo
    const tituloLinhas = doc.splitTextToSize(sanitize(tituloCompleto), pageWidth - 40);
    tituloLinhas.forEach((linha: string, idx: number) => {
      doc.text(linha, pageWidth / 2, pageHeight / 2 - 10 + (idx * 8), { align: 'center' });
    });
    
    doc.setFontSize(12);
    const dataExecucao = selectedExecucaoHistorica.executadoEm 
      ? new Date(selectedExecucaoHistorica.executadoEm).toLocaleString('pt-BR')
      : '-';
    doc.text(`Execução: ${dataExecucao}`, pageWidth / 2, pageHeight / 2 + 15, { align: 'center' });
    
    doc.setFontSize(10);
    const dataGeracao = new Date().toLocaleString('pt-BR');
    doc.text(`Gerado em: ${dataGeracao}`, pageWidth / 2, pageHeight - 30, { align: 'center' });

    // ===== NOVA PÁGINA - ÍNDICE =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Índice de Controles', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    const resultados = selectedExecucaoHistorica.resultados || {};
    const controlesArray: any[] = [];
    
    // resultados é um objeto onde cada chave contém um array de execuções
    Object.entries(resultados).forEach(([key, execucoes]: [string, any]) => {
      if (Array.isArray(execucoes)) {
        // Cada execução no array representa uma execução do mesmo controle
        execucoes.forEach((exec: any) => {
          // Recomendações podem estar em result.recommendations (array) ou controlRecommendation (string)
          let recomendacoesTexto = '';
          if (exec.result?.recommendations && Array.isArray(exec.result.recommendations)) {
            recomendacoesTexto = exec.result.recommendations.join('\n• ');
            if (recomendacoesTexto) recomendacoesTexto = '• ' + recomendacoesTexto;
          } else if (exec.controlRecommendation) {
            recomendacoesTexto = exec.controlRecommendation;
          }
          
          controlesArray.push({
            id: exec.controlId || key,
            nome: exec.controlName || key,
            severidade: exec.controlSeverity || '-',
            status: exec.result?.status || '-',
            violacoes: exec.result?.totalViolations || 0,
            descricao: exec.controlDescription || '',
            risco: exec.controlRisk || '',
            recomendacoes: recomendacoesTexto,
            timestamp: exec.timestamp || ''
          });
        });
      }
    });
    
    console.log('Total de controles para PDF:', controlesArray.length);

    // Mapa para armazenar a página de cada controle
    const controlePaginas: Record<number, number> = {};

    controlesArray.forEach((controle, index) => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      const linha = `${index + 1}. ${sanitize(controle.nome)} (${controle.severidade})`;
      doc.text(linha, 25, yPos);
      yPos += 6;
    });

    // ===== NOVA PÁGINA - INDICADORES E GRÁFICOS =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Indicadores e Gráficos', 20, yPos);
    yPos += 15;

    // Indicadores
    const resumo = selectedExecucaoHistorica.resumo || {};
    const totalControles = resumo.totalControles || 0;
    const controlesExecutados = resumo.controlesExecutados || 0;
    const totalViolacoes = resumo.totalViolacoes || 0;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    // Box de indicadores
    const boxWidth = 50;
    const boxHeight = 25;
    const boxSpacing = 10;
    let xPos = 20;

    // Total de Controles
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(240, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Controles', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(totalControles), xPos + boxWidth / 2, yPos + 20, { align: 'center' });

    xPos += boxWidth + boxSpacing;

    // Controles Executados
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(240, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Executados', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(controlesExecutados), xPos + boxWidth / 2, yPos + 20, { align: 'center' });

    xPos += boxWidth + boxSpacing;

    // Total de Violações
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Violações', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 53, 69);
    doc.text(String(totalViolacoes), xPos + boxWidth / 2, yPos + 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    yPos += boxHeight + 20;

    // Gráfico de severidade (Pizza)
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Controles com Violações por Severidade', 20, yPos);
    yPos += 10;

    const severidadeCount: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    controlesArray.forEach(c => {
      if (c.violacoes > 0 && c.severidade) {
        const sev = c.severidade.toLowerCase();
        if (severidadeCount[sev] !== undefined) {
          severidadeCount[sev]++;
        }
      }
    });

    const totalComViolacoes = Object.values(severidadeCount).reduce((sum, val) => sum + val, 0);
    
    if (totalComViolacoes > 0) {
      // Desenhar gráfico de pizza
      const centerX = 60;
      const centerY = yPos + 30;
      const radius = 25;
      
      const severidadeData = [
        { label: 'Crítica', value: severidadeCount.critical, color: [220, 53, 69] },
        { label: 'Alta', value: severidadeCount.high, color: [255, 193, 7] },
        { label: 'Média', value: severidadeCount.medium, color: [13, 202, 240] },
        { label: 'Baixa', value: severidadeCount.low, color: [25, 135, 84] }
      ].filter(s => s.value > 0);
      
      let currentAngle = 0;
      severidadeData.forEach((item) => {
        const sliceAngle = (item.value / totalComViolacoes) * 360;
        const startAngle = currentAngle;
        const endAngle = currentAngle + sliceAngle;
        
        // Desenhar fatia
        doc.setFillColor(item.color[0], item.color[1], item.color[2]);
        
        // Criar path da fatia
        const steps = Math.ceil(sliceAngle / 5);
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(1);
        
        for (let i = 0; i <= steps; i++) {
          const angle = startAngle + (sliceAngle * i / steps);
          const rad = (angle - 90) * Math.PI / 180;
          const x = centerX + radius * Math.cos(rad);
          const y = centerY + radius * Math.sin(rad);
          
          if (i === 0) {
            doc.line(centerX, centerY, x, y);
          } else {
            const prevAngle = startAngle + (sliceAngle * (i - 1) / steps);
            const prevRad = (prevAngle - 90) * Math.PI / 180;
            const prevX = centerX + radius * Math.cos(prevRad);
            const prevY = centerY + radius * Math.sin(prevRad);
            
            const points = [
              [centerX, centerY],
              [prevX, prevY],
              [x, y]
            ];
            
            doc.triangle(points[0][0], points[0][1], points[1][0], points[1][1], points[2][0], points[2][1], 'F');
          }
        }
        
        currentAngle = endAngle;
      });
      
      // Legenda ao lado
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let legendY = yPos + 10;
      severidadeData.forEach((item) => {
        doc.setFillColor(item.color[0], item.color[1], item.color[2]);
        doc.rect(100, legendY - 3, 8, 6, 'F');
        doc.setTextColor(0, 0, 0);
        const percent = Math.round((item.value / totalComViolacoes) * 100);
        doc.text(`${item.label}: ${item.value} (${percent}%)`, 112, legendY);
        legendY += 8;
      });
      
      yPos += 70;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Nenhuma violação encontrada', 30, yPos);
      yPos += 15;
    }

    // Top 5 Violações
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Top 5 Controles com Mais Violações', 20, yPos);
    yPos += 12;

    const top5 = [...controlesArray]
      .sort((a, b) => b.violacoes - a.violacoes)
      .slice(0, 5);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const maxViolacoes = Math.max(...top5.map(x => x.violacoes), 1);
    
    top5.forEach((c, idx) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      // Nome do controle com quebra de linha se necessário
      const nomeCompleto = sanitize(c.nome);
      const nomeLinhas = doc.splitTextToSize(nomeCompleto, 130);
      const numLinhas = nomeLinhas.length;
      
      doc.text(`${idx + 1}.`, 25, yPos);
      doc.text(nomeLinhas, 32, yPos);
      
      // Número de violações ao lado do nome
      doc.setFont('helvetica', 'bold');
      doc.text(String(c.violacoes), 185, yPos, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      
      // Barra de gráfico abaixo do texto
      const barY = yPos + (numLinhas * 4) + 2;
      const barWidth = (c.violacoes / maxViolacoes) * 100;
      if (barWidth > 0) {
        doc.setFillColor(220, 53, 69);
        doc.rect(32, barY, barWidth, 4, 'F');
      }
      
      yPos += (numLinhas * 4) + 10;
    });

    // ===== NOVA PÁGINA - CONTROLES E RESULTADOS =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Controles e Resultados', 20, yPos);
    yPos += 15;

    controlesArray.forEach((controle, index) => {
      // Verificar espaço
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      // Registrar a página onde o controle começa
      controlePaginas[index] = (doc as any).internal.getCurrentPageInfo().pageNumber;

      // Cabeçalho do controle
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${sanitize(controle.nome)}`, 20, yPos);
      yPos += 7;

      // ID do controle
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('ID:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(sanitize(controle.id), 45, yPos);
      yPos += 6;
      
      // Tabela com Severidade e Violações
      const infoTable = [
        [traduzirSeveridade(controle.severidade), String(controle.violacoes)]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [['Severidade', 'Violações']],
        body: infoTable,
        theme: 'striped',
        styles: { 
          fontSize: 11,
          cellPadding: 3,
          font: 'helvetica',
          halign: 'center'
        },
        headStyles: {
          fillColor: [80, 80, 80],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10
        },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' }
        },
        margin: { left: 20, right: 20 },
        tableWidth: 'auto'
      });

      yPos = (doc as any).lastAutoTable.finalY + 5;

      // Atributos do controle (descrição, risco, recomendações)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      
      if (controle.descricao) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Descrição:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const descSplit = doc.splitTextToSize(sanitize(controle.descricao), pageWidth - 60);
        doc.text(descSplit, 30, yPos);
        yPos += descSplit.length * 4 + 3;
        doc.setFont('helvetica', 'bold');
      }

      if (controle.risco) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Risco:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const riskSplit = doc.splitTextToSize(sanitize(controle.risco), pageWidth - 60);
        doc.text(riskSplit, 30, yPos);
        yPos += riskSplit.length * 4 + 3;
        doc.setFont('helvetica', 'bold');
      }

      if (controle.recomendacoes) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Recomendações:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const recSplit = doc.splitTextToSize(sanitize(controle.recomendacoes), pageWidth - 60);
        doc.text(recSplit, 30, yPos);
        yPos += recSplit.length * 4 + 3;
      }

      yPos += 5;
      doc.setDrawColor(220, 220, 220);
      doc.line(20, yPos, pageWidth - 20, yPos);
      yPos += 8;
    });

    // ===== ATUALIZAR ÍNDICE COM LINKS E PÁGINAS =====
    const indicePageNumber = 2;
    doc.setPage(indicePageNumber);
    yPos = 30;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    controlesArray.forEach((controle, index) => {
      if (yPos > pageHeight - 30) {
        yPos = 20;
      }
      
      const linha = `${index + 1}. ${sanitize(controle.nome)} (${controle.severidade})`;
      const paginaControle = controlePaginas[index];
      
      doc.setTextColor(0, 102, 204);
      doc.textWithLink(linha, 25, yPos, { pageNumber: paginaControle });
      
      doc.setTextColor(100, 100, 100);
      doc.text(`Pág. ${paginaControle}`, pageWidth - 25, yPos, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      
      yPos += 6;
    });

    // ===== RODAPÉ E LOGO EM TODAS AS PÁGINAS =====
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Logo no canto superior direito extremo (80% do tamanho)
      if (logoData) {
        try {
          doc.addImage(logoData, 'PNG', pageWidth - 27, 3, 24, 9.6);
        } catch (error) {
          console.warn('Erro ao adicionar logo:', error);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(180, 180, 180);
          doc.text('TrustSIS', pageWidth - 3, 10, { align: 'right' });
          doc.setTextColor(0, 0, 0);
        }
      } else {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(180, 180, 180);
        doc.text('TrustSIS', pageWidth - 3, 10, { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }
      
      // Rodapé centralizado
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Visão Executiva - ${sanitize(selectedExecucaoHistorica.politicaNome)} | Página ${i} de ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.setTextColor(0, 0, 0);
    }

    // Salvar PDF
    const nomeArquivo = `Visao_Executiva_${sanitize(selectedExecucaoHistorica.politicaNome)}_${new Date().getTime()}.pdf`;
    doc.save(nomeArquivo);
    
    showToast('PDF gerado com sucesso!', 'success');
  };

  const handleExportTechnical = async () => {
    if (!selectedExecucaoHistorica) {
      showToast('Nenhuma execução selecionada', 'danger');
      return;
    }

    const sanitize = (s: any) => (s ?? '')
      .toString()
      .normalize('NFC')
      .replace(/\u00A0/g, ' ');

    const traduzirSeveridade = (sev: string) => {
      const traducoes: Record<string, string> = {
        'critical': 'Crítica',
        'high': 'Alta',
        'medium': 'Média',
        'low': 'Baixa'
      };
      return traducoes[sev.toLowerCase()] || sev;
    };

    const doc = new jsPDF();
    doc.setFont('helvetica', 'normal');
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    let yPos = 20;

    // Carregar logo
    const logoUrl = '/assets/img/icons/logo2.png';
    let logoData: string | null = null;
    
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            logoData = canvas.toDataURL('image/png');
          }
          resolve();
        };
        img.onerror = reject;
        img.src = logoUrl;
      });
    } catch (error) {
      console.warn('Não foi possível carregar o logo:', error);
    }

    // ===== CAPA =====
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Visão Técnica', pageWidth / 2, pageHeight / 2 - 30, { align: 'center' });
    
    const nomeCliente = selectedExecucaoHistorica.clienteNome || 
                        clientesDisponiveis.find((c: any) => c._id === selectedExecucaoHistorica.clienteId)?.nome ||
                        'Cliente';
    const nomePolitica = selectedExecucaoHistorica.politicaNome || 'Política';
    const tituloCompleto = `${nomeCliente} - ${nomePolitica}`;
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'normal');
    const tituloLinhas = doc.splitTextToSize(sanitize(tituloCompleto), pageWidth - 40);
    tituloLinhas.forEach((linha: string, idx: number) => {
      doc.text(linha, pageWidth / 2, pageHeight / 2 - 10 + (idx * 8), { align: 'center' });
    });
    
    doc.setFontSize(12);
    const dataExecucao = selectedExecucaoHistorica.executadoEm 
      ? new Date(selectedExecucaoHistorica.executadoEm).toLocaleString('pt-BR')
      : '-';
    doc.text(`Execução: ${dataExecucao}`, pageWidth / 2, pageHeight / 2 + 15, { align: 'center' });
    
    doc.setFontSize(10);
    const dataGeracao = new Date().toLocaleString('pt-BR');
    doc.text(`Gerado em: ${dataGeracao}`, pageWidth / 2, pageHeight - 30, { align: 'center' });

    // ===== EXTRAIR CONTROLES =====
    const resultados = selectedExecucaoHistorica.resultados || {};
    const controlesArray: any[] = [];
    
    Object.entries(resultados).forEach(([key, execucoes]: [string, any]) => {
      if (Array.isArray(execucoes)) {
        execucoes.forEach((exec: any) => {
          let recomendacoesTexto = '';
          if (exec.result?.recommendations && Array.isArray(exec.result.recommendations)) {
            recomendacoesTexto = exec.result.recommendations.join('\n• ');
            if (recomendacoesTexto) recomendacoesTexto = '• ' + recomendacoesTexto;
          } else if (exec.controlRecommendation) {
            recomendacoesTexto = exec.controlRecommendation;
          }
          
          controlesArray.push({
            id: exec.controlId || key,
            nome: exec.controlName || key,
            severidade: exec.controlSeverity || '-',
            status: exec.result?.status || '-',
            violacoes: exec.result?.totalViolations || 0,
            descricao: exec.controlDescription || '',
            risco: exec.controlRisk || '',
            recomendacoes: recomendacoesTexto,
            timestamp: exec.timestamp || '',
            findings: exec.result?.findings || [],
            result: exec.result
          });
        });
      }
    });
    
    console.log('Total de controles para PDF Técnico:', controlesArray.length);

    // Mapa para armazenar a página de cada controle
    const controlePaginas: Record<number, number> = {};

    // ===== ÍNDICE =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Índice de Controles', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    controlesArray.forEach((controle, index) => {
      if (yPos > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
      }
      
      const linha = `${index + 1}. ${sanitize(controle.nome)} (${controle.severidade})`;
      doc.text(linha, 25, yPos);
      yPos += 6;
    });

    // ===== INDICADORES E GRÁFICOS =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Indicadores e Gráficos', 20, yPos);
    yPos += 15;

    const resumo = selectedExecucaoHistorica.resumo || {};
    const totalControles = resumo.totalControles || 0;
    const controlesExecutados = resumo.controlesExecutados || 0;
    const totalViolacoes = resumo.totalViolacoes || 0;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    const boxWidth = 50;
    const boxHeight = 25;
    const boxSpacing = 10;
    let xPos = 20;

    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(240, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Controles', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(totalControles), xPos + boxWidth / 2, yPos + 20, { align: 'center' });

    xPos += boxWidth + boxSpacing;

    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(240, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Executados', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(controlesExecutados), xPos + boxWidth / 2, yPos + 20, { align: 'center' });

    xPos += boxWidth + boxSpacing;

    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 240, 240);
    doc.rect(xPos, yPos, boxWidth, boxHeight, 'FD');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Total Violações', xPos + boxWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 53, 69);
    doc.text(String(totalViolacoes), xPos + boxWidth / 2, yPos + 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    yPos += boxHeight + 20;

    // Gráfico de severidade (Pizza) - mesmo código da visão executiva
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Controles com Violações por Severidade', 20, yPos);
    yPos += 10;

    const severidadeCount: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    controlesArray.forEach(c => {
      if (c.violacoes > 0 && c.severidade) {
        const sev = c.severidade.toLowerCase();
        if (severidadeCount[sev] !== undefined) {
          severidadeCount[sev]++;
        }
      }
    });

    const totalComViolacoes = Object.values(severidadeCount).reduce((sum, val) => sum + val, 0);
    
    if (totalComViolacoes > 0) {
      const centerX = 60;
      const centerY = yPos + 30;
      const radius = 25;
      
      const severidadeData = [
        { label: 'Crítica', value: severidadeCount.critical, color: [220, 53, 69] },
        { label: 'Alta', value: severidadeCount.high, color: [255, 193, 7] },
        { label: 'Média', value: severidadeCount.medium, color: [13, 202, 240] },
        { label: 'Baixa', value: severidadeCount.low, color: [25, 135, 84] }
      ].filter(s => s.value > 0);
      
      let currentAngle = 0;
      severidadeData.forEach((item) => {
        const sliceAngle = (item.value / totalComViolacoes) * 360;
        
        doc.setFillColor(item.color[0], item.color[1], item.color[2]);
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(1);
        
        const steps = Math.ceil(sliceAngle / 5);
        for (let i = 0; i <= steps; i++) {
          const angle = currentAngle + (sliceAngle * i / steps);
          const rad = (angle - 90) * Math.PI / 180;
          const x = centerX + radius * Math.cos(rad);
          const y = centerY + radius * Math.sin(rad);
          
          if (i === 0) {
            doc.line(centerX, centerY, x, y);
          } else {
            const prevAngle = currentAngle + (sliceAngle * (i - 1) / steps);
            const prevRad = (prevAngle - 90) * Math.PI / 180;
            const prevX = centerX + radius * Math.cos(prevRad);
            const prevY = centerY + radius * Math.sin(prevRad);
            
            doc.triangle(centerX, centerY, prevX, prevY, x, y, 'F');
          }
        }
        
        currentAngle += sliceAngle;
      });
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      let legendY = yPos + 10;
      severidadeData.forEach((item) => {
        doc.setFillColor(item.color[0], item.color[1], item.color[2]);
        doc.rect(100, legendY - 3, 8, 6, 'F');
        doc.setTextColor(0, 0, 0);
        const percent = Math.round((item.value / totalComViolacoes) * 100);
        doc.text(`${item.label}: ${item.value} (${percent}%)`, 112, legendY);
        legendY += 8;
      });
      
      yPos += 70;
    } else {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Nenhuma violação encontrada', 30, yPos);
      yPos += 15;
    }

    // Top 5 Violações
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Top 5 Controles com Mais Violações', 20, yPos);
    yPos += 12;

    const top5 = [...controlesArray]
      .sort((a, b) => b.violacoes - a.violacoes)
      .slice(0, 5);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const maxViolacoes = Math.max(...top5.map(x => x.violacoes), 1);
    
    top5.forEach((c, idx) => {
      if (yPos > pageHeight - 40) {
        doc.addPage();
        yPos = 20;
      }
      
      const nomeCompleto = sanitize(c.nome);
      const nomeLinhas = doc.splitTextToSize(nomeCompleto, 130);
      const numLinhas = nomeLinhas.length;
      
      doc.text(`${idx + 1}.`, 25, yPos);
      doc.text(nomeLinhas, 32, yPos);
      
      doc.setFont('helvetica', 'bold');
      doc.text(String(c.violacoes), 185, yPos, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      
      const barY = yPos + (numLinhas * 4) + 2;
      const barWidth = (c.violacoes / maxViolacoes) * 100;
      if (barWidth > 0) {
        doc.setFillColor(220, 53, 69);
        doc.rect(32, barY, barWidth, 4, 'F');
      }
      
      yPos += (numLinhas * 4) + 10;
    });

    // ===== CONTROLES E RESULTADOS COM ACHADOS =====
    doc.addPage();
    yPos = 20;
    
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Controles e Resultados Técnicos', 20, yPos);
    yPos += 15;

    controlesArray.forEach((controle, index) => {
      if (yPos > pageHeight - 80) {
        doc.addPage();
        yPos = 20;
      }

      // Registrar a página onde o controle começa
      controlePaginas[index] = (doc as any).internal.getCurrentPageInfo().pageNumber;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(`${index + 1}. ${sanitize(controle.nome)}`, 20, yPos);
      yPos += 7;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('ID:', 25, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(sanitize(controle.id), 45, yPos);
      yPos += 6;
      
      const infoTable = [
        [traduzirSeveridade(controle.severidade), String(controle.violacoes)]
      ];

      autoTable(doc, {
        startY: yPos,
        head: [['Severidade', 'Violações']],
        body: infoTable,
        theme: 'striped',
        styles: { 
          fontSize: 11,
          cellPadding: 3,
          font: 'helvetica',
          halign: 'center'
        },
        headStyles: {
          fillColor: [80, 80, 80],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10
        },
        columnStyles: {
          0: { cellWidth: 'auto' },
          1: { cellWidth: 'auto' }
        },
        margin: { left: 20, right: 20 },
        tableWidth: 'auto'
      });

      yPos = (doc as any).lastAutoTable.finalY + 5;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      
      if (controle.descricao) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Descrição:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const descSplit = doc.splitTextToSize(sanitize(controle.descricao), pageWidth - 60);
        doc.text(descSplit, 30, yPos);
        yPos += descSplit.length * 4 + 3;
        doc.setFont('helvetica', 'bold');
      }

      if (controle.risco) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Risco:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const riskSplit = doc.splitTextToSize(sanitize(controle.risco), pageWidth - 60);
        doc.text(riskSplit, 30, yPos);
        yPos += riskSplit.length * 4 + 3;
        doc.setFont('helvetica', 'bold');
      }

      if (controle.recomendacoes) {
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = 20;
        }
        doc.text('Recomendações:', 25, yPos);
        yPos += 5;
        doc.setFont('helvetica', 'normal');
        const recSplit = doc.splitTextToSize(sanitize(controle.recomendacoes), pageWidth - 60);
        doc.text(recSplit, 30, yPos);
        yPos += recSplit.length * 4 + 3;
      }

      // ===== TABELA DE ACHADOS (DIFERENCIAL DA VISÃO TÉCNICA) =====
      if (controle.findings && controle.findings.length > 0) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.text(`Achados Técnicos (${controle.findings.length})`, 25, yPos);
        yPos += 8;

        // Preparar dados da tabela de achados
        const findingsData = controle.findings.map((finding: any, idx: number) => {
          const detalhes: string[] = [];
          if (finding.data && Object.keys(finding.data).length > 0) {
            Object.entries(finding.data).forEach(([key, value]) => {
              detalhes.push(`${key}: ${JSON.stringify(value)}`);
            });
          }
          
          return [
            String(idx + 1),
            traduzirSeveridade(finding.severity || 'low'),
            sanitize(finding.description || '-'),
            detalhes.join('\n') || '-'
          ];
        });

        autoTable(doc, {
          startY: yPos,
          head: [['#', 'Severidade', 'Descrição', 'Detalhes']],
          body: findingsData,
          theme: 'striped',
          styles: { 
            fontSize: 8,
            cellPadding: 2,
            font: 'helvetica',
            overflow: 'linebreak',
            cellWidth: 'wrap'
          },
          headStyles: {
            fillColor: [80, 80, 80],
            textColor: 255,
            fontStyle: 'bold',
            fontSize: 9
          },
          columnStyles: {
            0: { cellWidth: 10, halign: 'center' },
            1: { cellWidth: 25, halign: 'center' },
            2: { cellWidth: 60 },
            3: { cellWidth: 80 }
          },
          margin: { left: 25, right: 20 }
        });

        yPos = (doc as any).lastAutoTable.finalY + 5;
      }

      yPos += 5;
      doc.setDrawColor(220, 220, 220);
      doc.line(20, yPos, pageWidth - 20, yPos);
      yPos += 8;
    });

    // ===== ATUALIZAR ÍNDICE COM LINKS E PÁGINAS =====
    const indicePageNumber = 2;
    doc.setPage(indicePageNumber);
    yPos = 30;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    
    controlesArray.forEach((controle, index) => {
      if (yPos > pageHeight - 30) {
        yPos = 20;
      }
      
      const linha = `${index + 1}. ${sanitize(controle.nome)} (${controle.severidade})`;
      const paginaControle = controlePaginas[index];
      
      doc.setTextColor(0, 102, 204);
      doc.textWithLink(linha, 25, yPos, { pageNumber: paginaControle });
      
      doc.setTextColor(100, 100, 100);
      doc.text(`Pág. ${paginaControle}`, pageWidth - 25, yPos, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      
      yPos += 6;
    });

    // ===== RODAPÉ E LOGO EM TODAS AS PÁGINAS =====
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      
      // Logo no canto superior direito extremo (80% do tamanho)
      if (logoData) {
        try {
          doc.addImage(logoData, 'PNG', pageWidth - 27, 3, 24, 9.6);
        } catch (error) {
          console.warn('Erro ao adicionar logo:', error);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(180, 180, 180);
          doc.text('TrustSIS', pageWidth - 3, 10, { align: 'right' });
          doc.setTextColor(0, 0, 0);
        }
      } else {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(180, 180, 180);
        doc.text('TrustSIS', pageWidth - 3, 10, { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }
      
      // Rodapé centralizado
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Visão Técnica - ${sanitize(selectedExecucaoHistorica.politicaNome)} | Página ${i} de ${totalPages}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
      doc.setTextColor(0, 0, 0);
    }

    const nomeArquivo = `Visao_Tecnica_${sanitize(selectedExecucaoHistorica.politicaNome)}_${new Date().getTime()}.pdf`;
    doc.save(nomeArquivo);
    
    showToast('PDF Técnico gerado com sucesso!', 'success');
  };

  const handleHistoricoSort = (field: string) => {
    if (historicoSortField === field) {
      setHistoricoSortOrder(historicoSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setHistoricoSortField(field);
      setHistoricoSortOrder('asc');
    }
    setCurrentHistoricoPage(1);
  };
  
  const renderHistoricoSortIcon = (field: string) => {
    if (historicoSortField !== field) {
      return (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1" style={{ opacity: 0.3 }}>
          <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
        </svg>
      );
    }
    return historicoSortOrder === 'asc' ? (
      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1">
        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1">
        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
      </svg>
    );
  };

  const handleClienteResultadosChange = (clienteId: string) => {
    setSelectedClientePoliticas(clienteId);
    if (clienteId) {
      loadExecucoesHistoricas(clienteId);
    } else {
      setExecucoesHistoricas([]);
    }
  };

  const handleViewExecucaoHistorica = async (execucaoId: string) => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner/execucoes/${execucaoId}` 
      : `/api/cowner/execucoes/${execucaoId}`;

    try {
      const response = await axios.get(
        url,
        { withCredentials: true }
      );
      setSelectedExecucaoHistorica(response.data.execucao);
      setShowExecucaoDetalhesModal(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes da execução:', error);
      setAlerta({ tipo: 'danger', mensagem: 'Erro ao carregar detalhes da execução' });
      setTimeout(() => setAlerta(null), 3000);
    }
  };

  const handleSalvarExecucao = async () => {
    if (!selectedClientePoliticas || Object.keys(execucaoResults).length === 0) {
      setAlerta({ tipo: 'warning', mensagem: 'Não há resultados para salvar' });
      setTimeout(() => setAlerta(null), 3000);
      return;
    }

    setSavingExecucao(true);

    try {
      // Para cada política executada, salvar uma execução
      for (const [politicaId, execucoes] of Object.entries(execucaoResults)) {
        const politica = politicasDisponiveis.find(p => p._id === politicaId);
        if (!politica) continue;

        // Calcular resumo da execução
        const totalControles = execucoes.length;
        const controlesExecutados = execucoes.filter(e => e.status === 'completed').length;
        const controlesFalhados = execucoes.filter(e => e.status === 'error').length;
        const totalViolacoes = execucoes
          .filter(e => e.status === 'completed' && e.result)
          .reduce((sum, e) => sum + (e.result?.totalViolations || 0), 0);
        const passou = controlesFalhados === 0 && 
                       execucoes.filter(e => e.status === 'completed' && e.result && !e.result.passed).length === 0;

        const sistema = sistemasDisponiveis.find(s => s.nome === selectedSistema);
        const executadoEm = new Date().toISOString();
        const inicioExecucao = execucoes[0]?.executedAt;
        const fimExecucao = execucoes[execucoes.length - 1]?.executedAt;
        let duracao = 0;
        if (inicioExecucao && fimExecucao) {
          duracao = new Date(fimExecucao).getTime() - new Date(inicioExecucao).getTime();
        }

        const payload = {
          politicaId,
          politicaNome: politica.nome,
          tipo: 'adhoc',
          sistemaId: sistema?.nome,
          sistemaNome: sistema?.nome,
          integrationId: sistema?.integrationId,
          resultados: { [politicaId]: execucoes },
          executadoEm,
          duracao: duracao || execucoes.reduce((sum, e) => sum + (e.executionTime || 0), 0),
          resumo: {
            totalControles,
            controlesExecutados,
            controlesFalhados,
            totalViolacoes,
            passou
          }
        };

        const base = process.env.REACT_APP_API_URL || '';
        const baseNoSlash = base ? base.replace(/\/$/, '') : '';
        const saveUrl = baseNoSlash 
          ? `${baseNoSlash}/api/cowner/cliente/${selectedClientePoliticas}/execucoes` 
          : `/api/cowner/cliente/${selectedClientePoliticas}/execucoes`;

        await axios.post(
          saveUrl,
          payload,
          { withCredentials: true }
        );
      }

      setAlerta({ tipo: 'success', mensagem: 'Execução salva com sucesso!' });
      setTimeout(() => setAlerta(null), 5000);
      
      // Exibir toast de confirmação
      showToast('Execução salva com sucesso!', 'success');

      // Limpar resultados após salvar
      setTimeout(() => {
        setExecucaoResults({});
        setSelectedPoliticas([]);
      }, 2000);
    } catch (error: any) {
      console.error('Erro ao salvar execução:', error);
      setAlerta({ 
        tipo: 'danger', 
        mensagem: `Erro ao salvar: ${error.response?.data?.error || error.message}` 
      });
      setTimeout(() => setAlerta(null), 5000);
    } finally {
      setSavingExecucao(false);
    }
  };

  const getSeverityBadgeClass = (severity: 'critical' | 'high' | 'medium' | 'low') => {
    const map = {
      critical: 'danger',
      high: 'warning',
      medium: 'info',
      low: 'success'
    };
    return `badge bg-${map[severity]}`;
  };

  const getSeverityLabel = (severity: 'critical' | 'high' | 'medium' | 'low') => {
    const map = {
      critical: 'Crítico',
      high: 'Alto',
      medium: 'Médio',
      low: 'Baixo'
    };
    return map[severity];
  };

  const openRegistroModal = () => {
    setShowRegistroModal(true);
    setStep('dados');
    setIsViewMode(false);
    setRequestId(''); // Limpar ID para criar nova solicitação
    setDadosSolicitacao({
      clienteId: '',
      tipoSolicitacao: '',
      nomeSolicitante: '',
      numeroSolicitacao: '',
      usuarioId: '',
      nomeUsuario: '',
      motivoSolicitacao: '',
      funcaoSAPSolicitada: '',
      sistema: ''
    });
    setPerguntaAtual(0);
    setRespostas({});
    setHistoricoPerguntasVisitadas([]);
    setTipoSolicitacaoAnterior('');
    loadClientesDisponiveis();
  };

  const handleClienteChange = (clienteId: string) => {
    setDadosSolicitacao({
      ...dadosSolicitacao,
      clienteId,
      tipoSolicitacao: '' // Reset tipo quando cliente muda
    });
  };

  const handleAvancarParaQuestionario = async () => {
    // Validar campos obrigatórios
    if (!dadosSolicitacao.clienteId || !dadosSolicitacao.tipoSolicitacao) {
      alert('Cliente e Tipo de Solicitação são obrigatórios');
      return;
    }

    // Carregar questionário baseado no cliente e tipo de solicitação
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const url = baseNoSlash 
      ? `${baseNoSlash}/api/cowner-questionarios/cliente/${dadosSolicitacao.clienteId}` 
      : `/api/cowner-questionarios/cliente/${dadosSolicitacao.clienteId}`;

    try {
      setLoading(true);
      const response = await axios.get(url, { withCredentials: true });
      
      // Filtrar questionários que contenham o tipo de solicitação selecionado
      const questionarioFiltrado = response.data.find((q: any) => 
        q.ativo && 
        q.tiposSolicitacao && 
        q.tiposSolicitacao.includes(dadosSolicitacao.tipoSolicitacao)
      );

      if (!questionarioFiltrado) {
        alert('Nenhum questionário ativo encontrado para este tipo de solicitação');
        return;
      }

      setQuestionario(questionarioFiltrado);
      setStep('questionario');
      setPerguntaAtual(0);
      setHistoricoPerguntasVisitadas([0]); // Inicializa histórico com primeira pergunta
      setTipoSolicitacaoAnterior(dadosSolicitacao.tipoSolicitacao); // Salva o tipo para comparação
    } catch (err: any) {
      console.error('Erro ao carregar questionário:', err);
      alert('Erro ao carregar questionário');
    } finally {
      setLoading(false);
    }
  };

  const handleVoltarParaFormulario = () => {
    setStep('dados');
  };

  const handleContinuarQuestionario = async () => {
    // Verificar se o tipo de solicitação mudou
    if (dadosSolicitacao.tipoSolicitacao !== tipoSolicitacaoAnterior) {
      // Tipo mudou - reiniciar questionário
      const confirmar = window.confirm(
        'O tipo de solicitação foi alterado. O progresso do questionário será perdido. Deseja continuar?'
      );
      
      if (!confirmar) {
        // Reverter para o tipo anterior
        setDadosSolicitacao({
          ...dadosSolicitacao,
          tipoSolicitacao: tipoSolicitacaoAnterior
        });
        setStep('questionario');
        return;
      }
      
      // Reiniciar questionário
      setRespostas({});
      setPerguntaAtual(0);
      setHistoricoPerguntasVisitadas([0]);
      await handleAvancarParaQuestionario();
    } else {
      // Tipo não mudou - apenas voltar para o questionário
      setStep('questionario');
    }
  };

  // Função para obter todas as perguntas alcançáveis a partir de um índice
  const obterPerguntasAlcancaveis = (indiceInicial: number, respostasAtuais: any): Set<string> => {
    if (!questionario) return new Set();
    
    console.log('[obterPerguntasAlcancaveis] Iniciando cálculo. Respostas atuais:', respostasAtuais);
    
    const alcancaveis = new Set<string>();
    const visitados = new Set<number>();
    const fila: number[] = [indiceInicial];
    
    while (fila.length > 0) {
      const indice = fila.shift()!;
      
      if (visitados.has(indice) || indice < 0 || indice >= questionario.perguntas.length) {
        continue;
      }
      
      visitados.add(indice);
      const pergunta = questionario.perguntas[indice];
      alcancaveis.add(pergunta.id);
      
      console.log(`[obterPerguntasAlcancaveis] Visitando pergunta ${indice}: ${pergunta.titulo} (ID: ${pergunta.id})`);
      
      const resposta = respostasAtuais[pergunta.id];
      console.log(`[obterPerguntasAlcancaveis] Resposta para esta pergunta:`, resposta);
      
      // Verificar roteamento condicional
      let proximoIndex = -1;
      
      if (pergunta.tipo === 'unica-escolha' && resposta) {
        const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(resposta));
        
        console.log(`[obterPerguntasAlcancaveis] Alternativa selecionada:`, alternativaSelecionada);
        
        if (alternativaSelecionada?.proximaPergunta) {
          const proximaPerguntaId = alternativaSelecionada.proximaPergunta;
          
          console.log(`[obterPerguntasAlcancaveis] proximaPergunta da alternativa: ${proximaPerguntaId}`);
          
          if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
            console.log(`[obterPerguntasAlcancaveis] Fim do questionário (FIM ou vazio) - PARANDO aqui`);
            // NÃO definir proximoIndex, deixar -1 para forçar a parada
            // E marcar explicitamente que não deve continuar
            proximoIndex = -999; // Valor especial para indicar FIM explícito
          } else {
            proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
            console.log(`[obterPerguntasAlcancaveis] Índice da próxima pergunta: ${proximoIndex}`);
          }
        } else {
          console.log(`[obterPerguntasAlcancaveis] Alternativa não tem proximaPergunta definida`);
        }
      } else if (pergunta.tipo === 'multipla-escolha' && Array.isArray(resposta) && resposta.length > 0) {
        for (const valor of resposta) {
          const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(valor));
          
          if (alternativaSelecionada?.proximaPergunta) {
            const proximaPerguntaId = alternativaSelecionada.proximaPergunta;
            
            if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
              proximoIndex = -999; // Valor especial para indicar FIM explícito
              break;
            } else {
              proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
              if (proximoIndex !== -1) break;
            }
          }
        }
      }
      // Para perguntas de texto-livre, número e data, não há roteamento condicional baseado em alternativas
      // Elas sempre vão para a próxima pergunta em ordem, a menos que tenha proximaPergunta configurada no objeto pergunta
      else if (pergunta.tipo === 'texto-livre' || pergunta.tipo === 'numero' || pergunta.tipo === 'data') {
        // Verificar se a pergunta em si tem uma propriedade proximaPergunta definida
        if (pergunta.proximaPergunta) {
          const proximaPerguntaId = pergunta.proximaPergunta;
          
          if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
            proximoIndex = -999; // FIM explícito
          } else {
            proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
          }
        }
        // Se não tem proximaPergunta, deixa proximoIndex = -1 para seguir fluxo normal (próxima em ordem)
      }
      
      // Se encontrou FIM explícito (-999), não continuar
      if (proximoIndex === -999) {
        console.log(`[obterPerguntasAlcancaveis] FIM explícito encontrado - não continuando para próxima pergunta`);
        // Não adiciona nada à fila, o fluxo para aqui
      }
      // Se não encontrou roteamento, ir para próxima em ordem
      else if (proximoIndex === -1 && indice < questionario.perguntas.length - 1) {
        proximoIndex = indice + 1;
        console.log(`[obterPerguntasAlcancaveis] Sem roteamento específico, indo para próxima em ordem: ${proximoIndex}`);
        fila.push(proximoIndex);
      } else if (proximoIndex === -1) {
        console.log(`[obterPerguntasAlcancaveis] Fim do fluxo (última pergunta ou sem próxima)`);
      } else if (proximoIndex > 0) {
        // proximoIndex válido (não -1 e não -999)
        fila.push(proximoIndex);
      }
    }
    
    console.log('[obterPerguntasAlcancaveis] Resultado final:', Array.from(alcancaveis));
    return alcancaveis;
  };

  const handleRespostaPergunta = async (perguntaId: string, valor: any) => {
    console.log(`[handleRespostaPergunta] Pergunta ID: ${perguntaId}, Valor: ${valor}`);
    
    const novasRespostas = {
      ...respostas,
      [perguntaId]: valor
    };
    
    console.log('[handleRespostaPergunta] Respostas antes de filtrar:', novasRespostas);
    
    // Calcular perguntas alcançáveis a partir do início com as novas respostas
    const perguntasAlcancaveis = obterPerguntasAlcancaveis(0, novasRespostas);
    
    console.log('[handleRespostaPergunta] Perguntas alcançáveis:', Array.from(perguntasAlcancaveis));
    
    // Remover respostas de perguntas que não são mais alcançáveis
    const respostasFiltradas: any = {};
    let removeuAlguma = false;
    
    Object.keys(novasRespostas).forEach(key => {
      if (perguntasAlcancaveis.has(key)) {
        respostasFiltradas[key] = novasRespostas[key];
        console.log(`[Limpeza] Mantendo resposta alcançável: ${key}`);
      } else {
        console.log(`[Limpeza] Removendo resposta da pergunta não alcançável: ${key}`);
        removeuAlguma = true;
      }
    });
    
    console.log('[handleRespostaPergunta] Respostas após filtrar:', respostasFiltradas);
    console.log('[handleRespostaPergunta] Removeu alguma?', removeuAlguma);
    
    setRespostas(respostasFiltradas);
    
    // CRUCIAL: Se removeu alguma resposta, salvar no banco imediatamente para sobrescrever
    if (removeuAlguma && requestId) {
      console.log('[Auto-save] Salvando respostas filtradas no banco após remoção...');
      console.log('[Auto-save] Request ID:', requestId);
      // Usar setTimeout para garantir que o estado seja atualizado primeiro
      setTimeout(async () => {
        await handleSalvarProgressoComRespostasFiltradas(respostasFiltradas);
        console.log('[Auto-save] Respostas filtradas salvas no banco!');
      }, 100);
    } else {
      console.log('[Auto-save] NÃO salvando:', { removeuAlguma, requestId });
    }
  };

  const handleProximaPergunta = () => {
    if (!questionario) return;

    const pergunta = questionario.perguntas[perguntaAtual];
    const resposta = respostas[pergunta.id];

    // Validar se a pergunta é obrigatória
    if (pergunta.obrigatoria) {
      if (!resposta || (Array.isArray(resposta) && resposta.length === 0) || resposta === '') {
        setAlerta({ tipo: 'danger', mensagem: 'Esta pergunta é obrigatória. Por favor, forneça uma resposta antes de avançar.' });
        setTimeout(() => setAlerta(null), 5000); // Remove o alerta após 5 segundos
        return;
      }
    }

    // Limpar respostas de perguntas não alcançáveis antes de avançar
    const perguntasAlcancaveis = obterPerguntasAlcancaveis(0, respostas);
    const respostasFiltradas: any = {};
    let removeuAlguma = false;
    
    Object.keys(respostas).forEach(key => {
      if (perguntasAlcancaveis.has(key)) {
        respostasFiltradas[key] = respostas[key];
      } else {
        console.log(`[Limpeza ao avançar] Removendo resposta da pergunta não alcançável: ${key}`);
        removeuAlguma = true;
      }
    });
    
    if (removeuAlguma) {
      setRespostas(respostasFiltradas);
    }

    let proximoIndex = -1;

    // Verificar se há roteamento condicional para única escolha
    if (pergunta.tipo === 'unica-escolha' && resposta) {
      // Buscar a alternativa selecionada pelo valor
      const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(resposta));
      
      console.log('Pergunta atual:', pergunta.titulo);
      console.log('Resposta selecionada:', resposta);
      console.log('Alternativa encontrada:', alternativaSelecionada);
      console.log('proximaPergunta:', alternativaSelecionada?.proximaPergunta);
      
      if (alternativaSelecionada?.proximaPergunta) {
        const proximaPerguntaId = alternativaSelecionada.proximaPergunta;
        
        // Se for FIM ou vazio, finaliza
        if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
          console.log('Finalizando questionário - proximaPergunta:', proximaPerguntaId);
          handleFinalizarQuestionario();
          return;
        }
        
        // Encontrar índice da próxima pergunta pelo ID
        proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
        console.log('Buscando pergunta com ID:', proximaPerguntaId, 'Índice encontrado:', proximoIndex);
        
        if (proximoIndex !== -1) {
          console.log('Indo para pergunta:', questionario.perguntas[proximoIndex].titulo);
        } else {
          console.warn('Pergunta com ID não encontrada:', proximaPerguntaId);
        }
      }
    }

    // Verificar se há roteamento condicional para múltipla escolha (usa a primeira alternativa selecionada)
    if (proximoIndex === -1 && pergunta.tipo === 'multipla-escolha' && Array.isArray(resposta) && resposta.length > 0) {
      // Pega a primeira alternativa selecionada que tenha proximaPergunta
      for (const valor of resposta) {
        const alternativaSelecionada = pergunta.alternativas.find((alt: any) => String(alt.valor) === String(valor));
        
        if (alternativaSelecionada?.proximaPergunta) {
          const proximaPerguntaId = alternativaSelecionada.proximaPergunta;
          
          if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
            handleFinalizarQuestionario();
            return;
          }
          
          proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
          if (proximoIndex !== -1) {
            break;
          }
        }
      }
    }

    // Para perguntas de texto-livre, número e data: verificar se há proximaPergunta no objeto pergunta
    if (proximoIndex === -1 && (pergunta.tipo === 'texto-livre' || pergunta.tipo === 'numero' || pergunta.tipo === 'data')) {
      if (pergunta.proximaPergunta) {
        const proximaPerguntaId = pergunta.proximaPergunta;
        
        if (proximaPerguntaId === 'FIM' || proximaPerguntaId === '') {
          handleFinalizarQuestionario();
          return;
        }
        
        proximoIndex = questionario.perguntas.findIndex((p: any) => p.id === proximaPerguntaId);
      }
      // Se não tem proximaPergunta, deixa proximoIndex = -1 para seguir fluxo normal (próxima em ordem)
    }

    // Se não encontrou roteamento condicional, ir para próxima em ordem
    if (proximoIndex === -1) {
      console.log('Indo para próxima em ordem. Atual:', perguntaAtual, 'Total:', questionario.perguntas.length);
      if (perguntaAtual < questionario.perguntas.length - 1) {
        proximoIndex = perguntaAtual + 1;
      } else {
        handleFinalizarQuestionario();
        return;
      }
    }

    // Adicionar ao histórico e navegar
    setHistoricoPerguntasVisitadas([...historicoPerguntasVisitadas, proximoIndex]);
    setPerguntaAtual(proximoIndex);
  };

  const handlePerguntaAnterior = () => {
    if (historicoPerguntasVisitadas.length > 1) {
      // Remover última pergunta do histórico
      const novoHistorico = [...historicoPerguntasVisitadas];
      novoHistorico.pop(); // Remove a pergunta atual
      
      // Pegar a pergunta anterior do histórico
      const perguntaAnteriorIndex = novoHistorico[novoHistorico.length - 1];
      
      setHistoricoPerguntasVisitadas(novoHistorico);
      setPerguntaAtual(perguntaAnteriorIndex);
      
      console.log('Voltando para pergunta do histórico:', perguntaAnteriorIndex);
      console.log('Histórico após voltar:', novoHistorico);
    }
  };

  // Função auxiliar para calcular o diagnóstico (pode ser usada ao finalizar ou ao carregar)
  const calcularDiagnostico = (questionarioData: any, respostasData: any) => {
    let pontuacao = 0;
    const recomendacoesEncontradas: string[] = [];
    let temRejeitarAutomaticamente = false;
    const alternativasRejeitadas: Array<{ pergunta: string; alternativa: string }> = [];
    
    console.log('[Diagnóstico] Iniciando cálculo de pontuação');
    console.log('[Diagnóstico] Respostas:', respostasData);
    console.log('[Diagnóstico] Questionário:', questionarioData);
    
    questionarioData.perguntas.forEach((pergunta: any) => {
      const resposta = respostasData[pergunta.id];
      
      console.log(`[Diagnóstico] Pergunta: ${pergunta.titulo}`);
      console.log(`[Diagnóstico] Tipo: ${pergunta.tipo}`);
      console.log(`[Diagnóstico] Resposta:`, resposta);
      
      if (resposta) {
        // Para perguntas de texto-livre, número ou data, não há alternativas nem pontuação
        if (pergunta.tipo === 'texto-livre' || pergunta.tipo === 'numero' || pergunta.tipo === 'data') {
          console.log(`[Diagnóstico] Pergunta de tipo ${pergunta.tipo} - não há pontuação ou alternativas`);
          // Verificar se há recomendação definida na própria pergunta (opcional)
          if (pergunta.recomendacao && pergunta.recomendacao.trim() !== '') {
            recomendacoesEncontradas.push(pergunta.recomendacao);
          }
          return; // Pular para próxima pergunta
        }
        
        // Se for múltipla escolha (array)
        if (Array.isArray(resposta)) {
          resposta.forEach((valorResp: string) => {
            const alt = pergunta.alternativas?.find((a: any) => String(a.valor) === String(valorResp));
            console.log(`[Diagnóstico] Alternativa encontrada:`, alt);
            if (alt) {
              // Verificar se esta alternativa força rejeição automática
              if (alt.rejeitarAutomaticamente) {
                console.log(`[Diagnóstico] ⚠️ REJEIÇÃO AUTOMÁTICA detectada na alternativa: ${alt.texto}`);
                temRejeitarAutomaticamente = true;
                alternativasRejeitadas.push({
                  pergunta: pergunta.titulo,
                  alternativa: alt.texto
                });
              }
              
              const pontos = alt.pontuacao || alt.valor || alt.pontos || 0;
              console.log(`[Diagnóstico] Pontos da alternativa:`, pontos);
              pontuacao += Number(pontos);
              if (alt.recomendacao && alt.recomendacao.trim() !== '') {
                recomendacoesEncontradas.push(alt.recomendacao);
              }
            }
          });
        } else {
          // Resposta única (unica-escolha)
          const alt = pergunta.alternativas?.find((a: any) => String(a.valor) === String(resposta));
          console.log(`[Diagnóstico] Alternativa encontrada (única):`, alt);
          if (alt) {
            // Verificar se esta alternativa força rejeição automática
            if (alt.rejeitarAutomaticamente) {
              console.log(`[Diagnóstico] ⚠️ REJEIÇÃO AUTOMÁTICA detectada na alternativa: ${alt.texto}`);
              temRejeitarAutomaticamente = true;
              alternativasRejeitadas.push({
                pergunta: pergunta.titulo,
                alternativa: alt.texto
              });
            }
            
            const pontos = alt.pontuacao || alt.valor || alt.pontos || 0;
            console.log(`[Diagnóstico] Pontos da alternativa:`, pontos);
            pontuacao += Number(pontos);
            if (alt.recomendacao && alt.recomendacao.trim() !== '') {
              recomendacoesEncontradas.push(alt.recomendacao);
            }
          }
        }
      }
    });
    
    console.log('[Diagnóstico] Pontuação total calculada:', pontuacao);
    console.log('[Diagnóstico] Tem rejeição automática?', temRejeitarAutomaticamente);
    
    const pontuacaoMinima = questionarioData.pontuacaoAprovacao || 0;
    const resultadoDiagnostico = temRejeitarAutomaticamente 
      ? 'rejeitado-automaticamente' 
      : (pontuacao >= pontuacaoMinima ? 'aprovado' : 'rejeitado');
    
    return {
      pontuacao,
      pontuacaoMinima,
      resultadoDiagnostico,
      recomendacoesEncontradas,
      temRejeitarAutomaticamente,
      alternativasRejeitadas
    };
  };

  const handleFinalizarQuestionario = async () => {
    if (!questionario) return;
    
    // Limpar respostas de perguntas não alcançáveis antes de finalizar
    const perguntasAlcancaveis = obterPerguntasAlcancaveis(0, respostas);
    const respostasFiltradas: any = {};
    
    console.log('[Diagnóstico] Perguntas alcançáveis:', Array.from(perguntasAlcancaveis));
    
    Object.keys(respostas).forEach(key => {
      if (perguntasAlcancaveis.has(key)) {
        respostasFiltradas[key] = respostas[key];
      } else {
        console.log(`[Diagnóstico - Limpeza] Removendo resposta da pergunta não alcançável: ${key}`);
      }
    });
    
    // Atualizar estado com respostas filtradas
    setRespostas(respostasFiltradas);
    
    // Calcular diagnóstico usando a função auxiliar
    const resultado = calcularDiagnostico(questionario, respostasFiltradas);
    
    // Salvar dados do diagnóstico
    setPontuacaoObtida(resultado.pontuacao);
    setPontuacaoAprovacao(resultado.pontuacaoMinima);
    setDiagnostico(resultado.resultadoDiagnostico as 'aprovado' | 'rejeitado' | 'rejeitado-automaticamente');
    setRecomendacoes(resultado.recomendacoesEncontradas);
    setRejeitadoAutomaticamente(resultado.temRejeitarAutomaticamente);
    setAlternativasComRejeitacao(resultado.alternativasRejeitadas);
    
    // IMPORTANTE: Primeiro atualizar o estado de respostas para garantir que o salvamento use as filtradas
    // Aguardar um momento para o estado ser atualizado
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Salvar no backend com respostas filtradas
    await handleSalvarProgressoComRespostasFiltradas(
      respostasFiltradas,
      resultado.pontuacao, 
      resultado.pontuacaoMinima, 
      resultado.resultadoDiagnostico, 
      resultado.recomendacoesEncontradas, 
      'diagnostico'
    );
    
    // Mudar para step de diagnóstico
    setStep('diagnostico');
  };

  const clienteSelecionado = clientesDisponiveis.find(c => c._id === dadosSolicitacao.clienteId);
  const tiposDisponiveis = clienteSelecionado?.tiposSolicitacao || [];

  // Função para ordenar requests
  const handleSort = (column: string, direction?: 'asc' | 'desc') => {
    if (direction) {
      setSortColumn(column);
      setSortDirection(direction);
    } else if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset para primeira página ao ordenar
  };

  // Filtrar requests com base no termo de pesquisa
  const filteredRequests = savedRequests.filter(request => {
    if (!Object.keys(columnFilters).length) return true;

    return Object.entries(columnFilters).every(([key, value]) => {
      if (!value) return true;
      const cell = String((request as any)[key] ?? '').toLowerCase();
      return cell.includes(value.toLowerCase());
    });
  });

  // Ordenar requests
  const sortedRequests = [...filteredRequests].sort((a, b) => {
    if (!sortColumn) return 0;
    
    let aValue: any = a[sortColumn];
    let bValue: any = b[sortColumn];
    
    // Tratamento especial para datas
    if (sortColumn === 'updatedAt' || sortColumn === 'createdAt') {
      aValue = new Date(aValue).getTime();
      bValue = new Date(bValue).getTime();
    }
    
    // Tratamento para valores numéricos
    if (sortColumn === 'pontuacaoObtida' || sortColumn === 'percentualConclusao') {
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
    }
    
    // Comparação
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginação
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = sortedRequests.slice(startIndex, endIndex);

  // Função para obter o status de uma pergunta (respondida, pulada ou pendente)
  const getStatusPergunta = (index: number): 'respondida' | 'pulada' | 'pendente' => {
    if (!questionario) return 'pendente';
    
    const pergunta = questionario.perguntas[index];
    const resposta = respostas[pergunta.id];
    
    // Verifica se tem resposta válida
    const temResposta = resposta !== undefined && 
                        resposta !== null && 
                        resposta !== '' && 
                        (Array.isArray(resposta) ? resposta.length > 0 : String(resposta).trim() !== '');
    
    if (temResposta) {
      return 'respondida';
    } else if (index < perguntaAtual) {
      // Se é anterior à pergunta atual e não tem resposta, foi pulada
      return 'pulada';
    } else {
      // Se é a atual ou posterior, é pendente
      return 'pendente';
    }
  };

  return (
    <>
      <div className="container py-5" style={{ marginTop: 80 }}>
        <div className="mb-4">
          <h1 className="mb-0">Cockpit - Compliance Owner</h1>
          <p className="lead mb-0">Gerencie suas solicitações de compliance</p>
        </div>

        <div className="row g-3">
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card h-100">
              <div className="card-body text-center">
                <div className="mb-3">
                  <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-primary" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 0a1 1 0 011 1v6h6a1 1 0 110 2H9v6a1 1 0 11-2 0V9H1a1 1 0 110-2h6V1a1 1 0 011-1z"/>
                  </svg>
                </div>
                <h5 className="card-title">Nova Solicitação</h5>
                <p className="card-text text-muted small mb-3">
                  Registre uma nova solicitação de compliance
                </p>
                <button 
                  className="btn btn-primary w-100"
                  onClick={openRegistroModal}
                >
                  Registrar Solicitação
                </button>
              </div>
            </div>
          </div>
          
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card h-100">
              <div className="card-body text-center">
                <div className="mb-3">
                  <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="text-success" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                  </svg>
                </div>
                <h5 className="card-title">Políticas de Compliance</h5>
                <p className="card-text text-muted small mb-3">
                  Execute e monitore políticas com controles SAP
                </p>
                <div className="d-flex flex-column gap-2">
                  <button 
                    className="btn btn-success w-100"
                    onClick={() => {
                      setShowPoliticasModal(true);
                      loadClientesDisponiveis();
                    }}
                  >
                    Executar Políticas
                  </button>
                  <button 
                    className="btn btn-outline-success w-100"
                    onClick={() => {
                      setShowResultadosModal(true);
                      loadClientesDisponiveis();
                    }}
                  >
                    Monitorar Execuções
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabela de Solicitações Salvas - Sem Card */}
      <div style={{ 
        width: '100%', 
        marginTop: '3rem', 
        marginBottom: '3rem',
        padding: '2rem 3rem',
        backgroundColor: 'var(--bs-body-bg)'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="mb-0">Solicitações Registradas</h3>
        </div>

        {/* Informações de resultados */}
        {savedRequests.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">
              Total: {savedRequests.length} solicitações
            </small>
            {sortColumn && (
              <small className="text-muted">
                Ordenado por: {sortColumn} ({sortDirection === 'asc' ? 'crescente' : 'decrescente'})
              </small>
            )}
          </div>
        )}
        {loadingRequests ? (
          <div className="text-center py-3">
            <Spinner />
          </div>
        ) : savedRequests.length === 0 ? (
          <div className="text-center py-5">
            <svg width="64" height="64" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" opacity="0.3">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M5 6a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm7 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM8 8a.5.5 0 0 0-.5.5v2.5a.5.5 0 0 0 1 0v-2.5A.5.5 0 0 0 8 8z"/>
            </svg>
            <p className="text-muted mb-0">Nenhuma solicitação salva</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center py-5">
            <svg width="64" height="64" viewBox="0 0 16 16" fill="currentColor" className="text-muted mb-3" opacity="0.3">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <p className="text-muted mb-2">Nenhum resultado encontrado com os filtros atuais</p>
            <button className="btn btn-sm btn-outline-secondary" onClick={() => setColumnFilters({})}>
              Limpar filtros
            </button>
          </div>
        ) : (
          <DataTable
            columns={[
              { key: 'clienteNome', label: 'Cliente', sortable: true },
              { key: 'tipoSolicitacao', label: 'Tipo', sortable: true },
              {
                key: 'status',
                label: 'Status',
                sortable: true,
                render: (value: any) => {
                  if (value === 'dados') return <span className="badge bg-secondary">Formulário preenchido</span>;
                  if (value === 'questionario') return <span className="badge bg-primary">Respondendo questionário</span>;
                  if (value === 'diagnostico') return <span className="badge bg-info">Diagnóstico gerado</span>;
                  if (value === 'completo') return <span className="badge bg-success">Completo</span>;
                  return <span className="text-muted">-</span>;
                }
              },
              { key: 'createdBy', label: 'Criado Por', sortable: true, render: (value: any) => value || '-' },
              { key: 'nomeSolicitante', label: 'Nome Solicitante', sortable: true, render: (value: any) => value || '-' },
              { key: 'numeroSolicitacao', label: 'Nº Solicitação', sortable: true, render: (value: any) => value || '-' },
              { key: 'usuarioId', label: 'ID Usuário', sortable: true, render: (value: any) => value || '-' },
              { key: 'nomeUsuario', label: 'Nome Usuário', sortable: true, render: (value: any) => value || '-' },
              { key: 'motivoSolicitacao', label: 'Motivo', sortable: true, render: (value: any) => <small>{value || '-'}</small> },
              { key: 'funcaoSAPSolicitada', label: 'Função SAP', sortable: true, render: (value: any) => value || '-' },
              { key: 'sistema', label: 'Sistema', sortable: true, render: (value: any) => value || '-' },
              {
                key: 'percentualConclusao',
                label: 'Progresso',
                sortable: true,
                render: (value: any) => (
                  <div className="d-flex align-items-center gap-2">
                    <div className="progress flex-grow-1" style={{ height: '8px', minWidth: '80px' }}>
                      <div className="progress-bar bg-success" style={{ width: `${Number(value) || 0}%` }} />
                    </div>
                    <small className="text-muted">{Number(value) || 0}%</small>
                  </div>
                )
              },
              {
                key: 'pontuacaoObtida',
                label: 'Pontuação',
                sortable: true,
                render: (value: any, row: any) => value !== undefined ? `${value} / ${row.pontuacaoAprovacao || 0}` : '-'
              },
              {
                key: 'diagnostico',
                label: 'Diagnóstico',
                sortable: true,
                render: (value: any) => {
                  if (value === 'aprovado') return <span className="badge bg-success">Aprovado</span>;
                  if (value === 'rejeitado') return <span className="badge bg-danger">Rejeitado</span>;
                  if (value === 'rejeitado-automaticamente') return <span className="badge bg-danger">Rejeitado Automaticamente</span>;
                  return <span className="text-muted">-</span>;
                }
              },
              {
                key: 'updatedAt',
                label: 'Última Atualização',
                sortable: true,
                render: (value: any) => <small>{value ? new Date(value).toLocaleString('pt-BR') : '-'}</small>
              },
              {
                key: 'acoes',
                label: 'Ações',
                sortable: false,
                minWidth: 100,
                render: (_value: any, row: any) => (
                  <ActionMenu
                    actions={[
                      {
                        key: 'view',
                        label: 'Visualizar',
                        icon: Icons.Eye,
                        onClick: () => handleVisualizarRequest(row._id)
                      },
                      {
                        key: 'open',
                        label: 'Abrir/Retomar',
                        icon: Icons.ArrowRight,
                        onClick: () => handleCarregarRequest(row._id)
                      },
                      {
                        key: 'delete',
                        label: 'Deletar',
                        icon: Icons.Trash,
                        variant: 'danger',
                        onClick: () => handleDeletarRequest(row._id)
                      }
                    ]}
                  />
                )
              }
            ]}
            initialVisibleColumns={{
              clienteNome: visibleColumns.cliente,
              tipoSolicitacao: visibleColumns.tipo,
              status: visibleColumns.status,
              percentualConclusao: visibleColumns.progresso,
              updatedAt: visibleColumns.ultimaAtualizacao,
              acoes: visibleColumns.acoes,
              createdBy: visibleColumns.createdBy,
              nomeSolicitante: visibleColumns.nomeSolicitante,
              numeroSolicitacao: visibleColumns.numeroSolicitacao,
              usuarioId: visibleColumns.usuarioId,
              nomeUsuario: visibleColumns.nomeUsuario,
              motivoSolicitacao: visibleColumns.motivoSolicitacao,
              funcaoSAPSolicitada: visibleColumns.funcaoSAPSolicitada,
              sistema: visibleColumns.sistema,
              pontuacaoObtida: visibleColumns.pontuacaoObtida,
              diagnostico: visibleColumns.diagnostico
            }}
            data={paginatedRequests}
            keyExtractor={(row: any, index: number) => row._id || row.requestId || index}
            sortColumn={sortColumn || null}
            sortDirection={sortDirection}
            onSort={(column: string, direction?: 'asc' | 'desc') => handleSort(column, direction)}
            filters={columnFilters}
            onFilter={(columnKey: string, value: string) => {
              setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
              setCurrentPage(1);
            }}
            striped
            hover
            responsive
            emptyMessage="Nenhuma solicitação encontrada"
            pagination
            currentPage={currentPage}
            pageSize={itemsPerPage}
            totalItems={sortedRequests.length}
            onPageChange={(page: number) => setCurrentPage(page)}
            onPageSizeChange={(size: number) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 25, 50, 100]}
          />
        )}
      </div>

      {/* Modal: Registrar Solicitação */}
      {showRegistroModal && (
        <div className="cowner-modal-overlay">
          <div 
            className="cowner-modal" 
            style={{ maxWidth: '900px', maxHeight: '90vh' }}
          >
            <div className="cowner-modal-header">
              {isViewMode ? (
                <h3>
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                  </svg>
                  Visualizar Solicitação
                </h3>
              ) : step === 'dados' ? (
                <h3>Registrar Solicitação - Dados</h3>
              ) : step === 'diagnostico' ? (
                <h3>Diagnóstico</h3>
              ) : (
                <div>
                  <h3 className="mb-1">Questionário</h3>
                  <small className="text-muted">{questionario?.titulo || ''}</small>
                </div>
              )}
              <button className="cowner-modal-close" onClick={() => setShowRegistroModal(false)} aria-label="Fechar">
                ×
              </button>
            </div>

            <div className="cowner-modal-body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : isViewMode ? (
                // Modo Visualização: Mostra tudo em uma única tela rolável
                <div>
                  {/* Seção de Dados */}
                  <div className="mb-5 pb-4 border-bottom">
                    <h5 className="mb-4">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                        <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zm8 0A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm-8 8A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm8 0A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3z"/>
                      </svg>
                      Dados da Solicitação
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Cliente</label>
                        <p className="form-control-plaintext">{clientesDisponiveis.find(c => c._id === dadosSolicitacao.clienteId)?.nome || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Tipo de Solicitação</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.tipoSolicitacao || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Nome do Solicitante</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.nomeSolicitante || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Número da Solicitação</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.numeroSolicitacao || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Usuário (ID)</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.usuarioId || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Nome do Usuário</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.nomeUsuario || '-'}</p>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-bold">Motivo da Solicitação</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.motivoSolicitacao || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Função SAP Solicitada</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.funcaoSAPSolicitada || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Sistema</label>
                        <p className="form-control-plaintext">{dadosSolicitacao.sistema || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Seção de Diagnóstico (se existir) */}
                  {diagnostico && (
                    <div>
                      <h5 className="mb-4">
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M11 5.5a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1zm-2.5 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H9a.5.5 0 0 1-.5-.5v-1zm-5 0A.5.5 0 0 1 4 5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5v-1zM3.5 8a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1zM6 8.5A.5.5 0 0 1 6.5 8h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3 0a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1-.5-.5v-1z"/>
                          <path d="M2 1a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V1zm11 0H3v14h10V1z"/>
                        </svg>
                        Diagnóstico
                      </h5>

                      {/* Resultado da Pontuação */}
                      <div 
                        className="mb-4"
                        style={{
                          backgroundColor: '#0c5460',
                          color: 'white',
                          padding: '1.25rem',
                          borderRadius: '0.375rem',
                          border: 'none'
                        }}
                      >
                        <h5 className="mb-3" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                            <path d="M8.5 5.5a.5.5 0 0 0-1 0v3.362l-1.429 2.38a.5.5 0 1 0 .858.515l1.5-2.5A.5.5 0 0 0 8.5 9V5.5z"/>
                            <path d="M6.5 0a.5.5 0 0 0 0 1H7v1.07a7.001 7.001 0 0 0-3.273 12.474l-.602.602a.5.5 0 0 0 .707.708l.746-.746A6.97 6.97 0 0 0 8 16a6.97 6.97 0 0 0 3.422-.892l.746.746a.5.5 0 0 0 .707-.708l-.601-.602A7.001 7.001 0 0 0 9 2.07V1h.5a.5.5 0 0 0 0-1h-3zm1.038 3.018a6.093 6.093 0 0 1 .924 0 6 6 0 1 1-.924 0zM8.5 5.5v3.362l-1.429 2.38a.5.5 0 1 0 .858.515l1.5-2.5A.5.5 0 0 0 8.5 9V5.5z"/>
                          </svg>
                          Análise de Pontuação
                        </h5>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <strong style={{ color: 'white', fontSize: '0.75rem' }}>Pontuação Obtida:</strong>
                            <h4 className="mb-0 mt-1" style={{ color: 'white', fontSize: '1.5rem' }}>{pontuacaoObtida} pontos</h4>
                          </div>
                          <div className="col-md-6">
                            <strong style={{ color: 'white', fontSize: '0.75rem' }}>Pontuação Mínima para Aprovação:</strong>
                            <h4 className="mb-0 mt-1" style={{ color: 'white', fontSize: '1.5rem' }}>{pontuacaoAprovacao} pontos</h4>
                          </div>
                        </div>
                      </div>

                      {/* Resultado do Diagnóstico */}
                      <div 
                        className="mb-4" 
                        style={{ 
                          backgroundColor: diagnostico === 'aprovado' ? '#1e7e34' : '#bd2130',
                          color: 'white',
                          padding: '1.25rem',
                          borderRadius: '0.375rem',
                          border: 'none'
                        }}
                      >
                        <h5 className="mb-2" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                          {diagnostico === 'aprovado' ? (
                            <>
                              <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                              </svg>
                              Recomendação: APROVAR
                            </>
                          ) : diagnostico === 'rejeitado-automaticamente' ? (
                            <>
                              <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                              </svg>
                              Recomendação: REJEITAR
                            </>
                          ) : (
                            <>
                              <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                              </svg>
                              Recomendação: REJEITAR
                            </>
                          )}
                        </h5>
                        <p className="mb-0" style={{ color: 'white', fontSize: '0.85rem' }}>
                          {diagnostico === 'aprovado' 
                            ? 'A solicitação atingiu a pontuação mínima necessária e pode ser aprovada.'
                            : diagnostico === 'rejeitado-automaticamente'
                              ? 'A solicitação foi REJEITADA AUTOMATICAMENTE devido a uma ou mais respostas marcadas como críticas.'
                              : 'A solicitação não atingiu a pontuação mínima necessária. Recomenda-se rejeitar ou solicitar ajustes.'}
                        </p>
                      </div>

                      {/* Alerta de Rejeição Automática */}
                      {rejeitadoAutomaticamente && (
                        <div 
                          className="mb-4" 
                          style={{ 
                            backgroundColor: '#bd2130',
                            color: 'white',
                            padding: '1.25rem',
                            borderRadius: '0.375rem',
                            borderLeft: '4px solid #721c24'
                          }}
                        >
                          <h6 className="mb-2" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="white" className="me-2">
                              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                            </svg>
                            Rejeição automática aplicada!
                          </h6>
                          <p className="mb-2" style={{ color: 'white', fontSize: '0.85rem' }}>
                            Uma ou mais respostas selecionadas foram marcadas como críticas e forçam a rejeição automática da solicitação, 
                            independente da pontuação obtida.
                          </p>
                          {alternativasComRejeitacao.length > 0 && (
                            <div className="mt-3">
                              <strong className="d-block mb-2" style={{ color: 'white', fontSize: '0.85rem' }}>Respostas críticas identificadas:</strong>
                              <ul className="mb-0" style={{ listStyle: 'none', paddingLeft: '0' }}>
                                {alternativasComRejeitacao.map((item, index) => (
                                  <li key={index} className="mb-2 p-2" style={{ 
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                                    borderLeft: '3px solid white',
                                    borderRadius: '4px',
                                    color: 'white',
                                    fontSize: '0.75rem'
                                  }}>
                                    <strong style={{ color: 'white' }}>Pergunta:</strong> {item.pergunta}
                                    <br />
                                    <strong style={{ color: 'white' }}>Resposta:</strong> {item.alternativa}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Recomendações */}
                      {recomendacoes.length > 0 && (
                        <div 
                          className="mb-4" 
                          style={{ 
                            backgroundColor: '#d97706',
                            color: 'white',
                            padding: '1.25rem',
                            borderRadius: '0.375rem',
                            border: 'none'
                          }}
                        >
                          <h6 className="mb-3" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                            <svg width="20" height="20" viewBox="0 0 16 16" fill="white" className="me-2">
                              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                            </svg>
                            Recomendações e Pontos de Atenção:
                          </h6>
                          <ul className="mb-0" style={{ color: 'white', fontSize: '0.85rem' }}>
                            {recomendacoes.map((rec, index) => (
                              <li key={index}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Resumo das Respostas */}
                      <div className="mt-4">
                        <h6 className="mb-3 d-flex align-items-center">
                          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                            <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                          </svg>
                          Resumo das Respostas
                        </h6>
                        <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' }}>
                          {questionario && questionario.perguntas.map((pergunta: any, index: number) => {
                            const resposta = respostas[pergunta.id];
                            if (!resposta) return null;

                            let respostaTexto = '';
                            if (Array.isArray(resposta)) {
                              respostaTexto = resposta.map(r => {
                                const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(r));
                                return alt?.texto || r;
                              }).join(', ');
                            } else {
                              const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(resposta));
                              respostaTexto = alt?.texto || resposta;
                            }

                            const temRejeitacao = alternativasComRejeitacao.some(
                              item => item.pergunta === pergunta.titulo && item.alternativa === respostaTexto
                            );

                            return (
                              <div 
                                key={index} 
                                className="mb-3 pb-3 border-bottom"
                                style={temRejeitacao ? {
                                  borderLeft: '4px solid #bd2130',
                                  backgroundColor: '#bd2130',
                                  paddingLeft: '12px',
                                  marginLeft: '-4px',
                                  borderRadius: '4px',
                                  padding: '12px'
                                } : {}}
                              >
                                <strong className="d-block mb-2" style={temRejeitacao ? { color: 'white' } : {}}>
                                  {temRejeitacao && (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="white" className="me-2">
                                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                    </svg>
                                  )}
                                  {pergunta.titulo}
                                </strong>
                                <div 
                                  className="text-muted" 
                                  style={temRejeitacao ? { 
                                    color: 'white !important',
                                    fontWeight: 'bold'
                                  } : {}}
                                >
                                  {respostaTexto}
                                </div>
                                {temRejeitacao && (
                                  <small 
                                    className="d-block mt-2" 
                                    style={{ 
                                      color: 'white',
                                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                      padding: '6px 10px',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    ⚠️ Resposta crítica - Rejeição automática
                                  </small>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : step === 'dados' ? (
                // Etapa 1: Dados da Solicitação
                <div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="cliente" className="form-label">
                        Cliente <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="cliente"
                        value={dadosSolicitacao.clienteId}
                        onChange={(e) => handleClienteChange(e.target.value)}
                        disabled={isViewMode}
                        required
                      >
                        <option value="">Selecione um cliente</option>
                        {clientesDisponiveis.map((cliente) => (
                          <option key={cliente._id} value={cliente._id}>
                            {cliente.nome}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="tipoSolicitacao" className="form-label">
                        Tipo de Solicitação <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="tipoSolicitacao"
                        value={dadosSolicitacao.tipoSolicitacao}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, tipoSolicitacao: e.target.value })}
                        disabled={isViewMode || !dadosSolicitacao.clienteId}
                        required
                      >
                        <option value="">Selecione um tipo</option>
                        {tiposDisponiveis.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {tipo}
                          </option>
                        ))}
                      </select>
                      {!dadosSolicitacao.clienteId && (
                        <small className="text-muted">Selecione um cliente primeiro</small>
                      )}
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="nomeSolicitante" className="form-label">Nome do Solicitante</label>
                      <input
                        type="text"
                        className="form-control"
                        id="nomeSolicitante"
                        value={dadosSolicitacao.nomeSolicitante}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, nomeSolicitante: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="numeroSolicitacao" className="form-label">Número da Solicitação</label>
                      <input
                        type="text"
                        className="form-control"
                        id="numeroSolicitacao"
                        value={dadosSolicitacao.numeroSolicitacao}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, numeroSolicitacao: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="usuarioId" className="form-label">Usuário (ID)</label>
                      <input
                        type="text"
                        className="form-control"
                        id="usuarioId"
                        value={dadosSolicitacao.usuarioId}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, usuarioId: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="nomeUsuario" className="form-label">Nome do Usuário</label>
                      <input
                        type="text"
                        className="form-control"
                        id="nomeUsuario"
                        value={dadosSolicitacao.nomeUsuario}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, nomeUsuario: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="motivoSolicitacao" className="form-label">Motivo da Solicitação</label>
                      <textarea
                        className="form-control"
                        id="motivoSolicitacao"
                        rows={3}
                        value={dadosSolicitacao.motivoSolicitacao}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, motivoSolicitacao: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="funcaoSAPSolicitada" className="form-label">Função SAP Solicitada</label>
                      <input
                        type="text"
                        className="form-control"
                        id="funcaoSAPSolicitada"
                        value={dadosSolicitacao.funcaoSAPSolicitada}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, funcaoSAPSolicitada: e.target.value })}
                        disabled={isViewMode}
                      />
                    </div>

                    <div className="col-md-6">
                      <label htmlFor="sistema" className="form-label">Sistema</label>
                      <select
                        className="form-select"
                        id="sistema"
                        value={dadosSolicitacao.sistema}
                        onChange={(e) => setDadosSolicitacao({ ...dadosSolicitacao, sistema: e.target.value })}
                        disabled={isViewMode}
                      >
                        <option value="">Selecione um sistema...</option>
                        {clienteSelecionado?.sistemas?.map((sistema: any, index: number) => (
                          <option key={index} value={sistema.nome} title={sistema.descricao}>
                            {sistema.nome}
                          </option>
                        ))}
                      </select>
                      {clienteSelecionado && dadosSolicitacao.sistema && clienteSelecionado.sistemas?.find((s: any) => s.nome === dadosSolicitacao.sistema)?.descricao && (
                        <small className="form-text text-muted">
                          {clienteSelecionado.sistemas.find((s: any) => s.nome === dadosSolicitacao.sistema)?.descricao}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              ) : step === 'questionario' && questionario ? (
                // Etapa 2: Questionário (Wizard)
                <div>
                  {/* Barra de Progresso */}
                  {(() => {
                    const totalPerguntas = questionario.perguntas.length;
                    const perguntasRespondidas = questionario.perguntas.filter((p, index) => {
                      const status = getStatusPergunta(index);
                      return status === 'respondida' || status === 'pulada';
                    }).length;
                    const percentual = Math.round((perguntasRespondidas / totalPerguntas) * 100);

                    return (
                      <div className="progress-container mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">
                            Pergunta {perguntaAtual + 1} de {totalPerguntas}
                          </span>
                          <span className="fw-bold" style={{ fontSize: '1.1rem', color: '#198754' }}>
                            {percentual}%
                          </span>
                        </div>
                        <div className="progress" style={{ height: '12px' }}>
                          <div 
                            className="progress-bar bg-success" 
                            role="progressbar" 
                            style={{ width: `${percentual}%` }}
                            aria-valuenow={percentual} 
                            aria-valuemin={0} 
                            aria-valuemax={100}
                          />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Alerta */}
                  {alerta && (
                    <div className={`alert alert-${alerta.tipo} alert-dismissible fade show mb-4`} role="alert">
                      {alerta.tipo === 'danger' && (
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2" style={{ verticalAlign: 'text-bottom' }}>
                          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                        </svg>
                      )}
                      {alerta.tipo === 'success' && (
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2" style={{ verticalAlign: 'text-bottom' }}>
                          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                        </svg>
                      )}
                      {alerta.mensagem}
                      <button type="button" className="btn-close" onClick={() => setAlerta(null)} aria-label="Fechar"></button>
                    </div>
                  )}

                  {(() => {
                    const pergunta = questionario.perguntas[perguntaAtual];
                    if (!pergunta) return null;

                    return (
                      <div>
                        <div className="mb-4">
                          <h5 className="mb-2">
                            {pergunta.titulo}
                            {pergunta.obrigatoria && <span className="text-danger ms-1">*</span>}
                          </h5>
                          {pergunta.descricao && (
                            <p className="text-muted small mb-2">{pergunta.descricao}</p>
                          )}
                          {pergunta.obrigatoria && (
                            <p className="text-muted small mb-2">
                              <small><em>* Campo obrigatório</em></small>
                            </p>
                          )}
                          {pergunta.informacaoAjuda && (
                            <div className="alert alert-info mb-3 info-ajuda">
                              <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                              </svg>
                              <span style={{ whiteSpace: 'pre-wrap' }}>{pergunta.informacaoAjuda}</span>
                            </div>
                          )}
                        </div>

                        {/* Renderizar campos baseado no tipo */}
                        {pergunta.tipo === 'unica-escolha' && (
                          <div>
                            {pergunta.alternativas.map((alt: any, idx: number) => (
                              <label key={idx} className="alternativa-item form-check mb-2" htmlFor={`alt-${pergunta.id}-${idx}`}>
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name={`pergunta-${pergunta.id}`}
                                  id={`alt-${pergunta.id}-${idx}`}
                                  value={alt.valor}
                                  checked={String(respostas[pergunta.id]) === String(alt.valor)}
                                  onChange={(e) => handleRespostaPergunta(pergunta.id, e.target.value)}
                                />
                                <span className="form-check-label">
                                  {alt.texto}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {pergunta.tipo === 'multipla-escolha' && (
                          <div>
                            {pergunta.alternativas.map((alt: any, idx: number) => (
                              <label key={idx} className="alternativa-item form-check mb-2" htmlFor={`alt-${pergunta.id}-${idx}`}>
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`alt-${pergunta.id}-${idx}`}
                                  value={alt.valor}
                                  checked={(respostas[pergunta.id] || []).includes(alt.valor)}
                                  onChange={(e) => {
                                    const valores = respostas[pergunta.id] || [];
                                    if (e.target.checked) {
                                      handleRespostaPergunta(pergunta.id, [...valores, alt.valor]);
                                    } else {
                                      handleRespostaPergunta(pergunta.id, valores.filter((v: any) => v !== alt.valor));
                                    }
                                  }}
                                />
                                <span className="form-check-label">
                                  {alt.texto}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {pergunta.tipo === 'texto-livre' && (
                          <textarea
                            className="form-control"
                            rows={4}
                            value={respostas[pergunta.id] || ''}
                            onChange={(e) => handleRespostaPergunta(pergunta.id, e.target.value)}
                            placeholder="Digite sua resposta..."
                          />
                        )}

                        {pergunta.tipo === 'numero' && (
                          <input
                            type="number"
                            className="form-control"
                            value={respostas[pergunta.id] || ''}
                            onChange={(e) => handleRespostaPergunta(pergunta.id, e.target.value)}
                            placeholder="Digite um número..."
                          />
                        )}

                        {pergunta.tipo === 'data' && (
                          <input
                            type="date"
                            className="form-control"
                            value={respostas[pergunta.id] || ''}
                            onChange={(e) => handleRespostaPergunta(pergunta.id, e.target.value)}
                          />
                        )}
                      </div>
                    );
                  })()}
                </div>
              ) : step === 'diagnostico' ? (
                // Etapa 3: Diagnóstico
                <div>
                  {/* Resultado da Pontuação */}
                  <div 
                    className="mb-4"
                    style={{
                      backgroundColor: '#0c5460',
                      color: 'white',
                      padding: '1.25rem',
                      borderRadius: '0.375rem',
                      border: 'none'
                    }}
                  >
                    <h5 className="mb-3" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                      <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                        <path d="M8.5 5.5a.5.5 0 0 0-1 0v3.362l-1.429 2.38a.5.5 0 1 0 .858.515l1.5-2.5A.5.5 0 0 0 8.5 9V5.5z"/>
                        <path d="M6.5 0a.5.5 0 0 0 0 1H7v1.07a7.001 7.001 0 0 0-3.273 12.474l-.602.602a.5.5 0 0 0 .707.708l.746-.746A6.97 6.97 0 0 0 8 16a6.97 6.97 0 0 0 3.422-.892l.746.746a.5.5 0 0 0 .707-.708l-.601-.602A7.001 7.001 0 0 0 9 2.07V1h.5a.5.5 0 0 0 0-1h-3zm1.038 3.018a6.093 6.093 0 0 1 .924 0 6 6 0 1 1-.924 0zM8.5 5.5v3.362l-1.429 2.38a.5.5 0 1 0 .858.515l1.5-2.5A.5.5 0 0 0 8.5 9V5.5z"/>
                      </svg>
                      Análise de Pontuação
                    </h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <strong style={{ color: 'white', fontSize: '0.75rem' }}>Pontuação Obtida:</strong>
                        <h4 className="mb-0 mt-1" style={{ color: 'white', fontSize: '1.5rem' }}>{pontuacaoObtida} pontos</h4>
                      </div>
                      <div className="col-md-6">
                        <strong style={{ color: 'white', fontSize: '0.75rem' }}>Pontuação Mínima para Aprovação:</strong>
                        <h4 className="mb-0 mt-1" style={{ color: 'white', fontSize: '1.5rem' }}>{pontuacaoAprovacao} pontos</h4>
                      </div>
                    </div>
                  </div>

                  {/* Resultado do Diagnóstico */}
                  <div 
                    className="mb-4" 
                    style={{ 
                      backgroundColor: diagnostico === 'aprovado' ? '#1e7e34' : '#bd2130',
                      color: 'white',
                      padding: '1.25rem',
                      borderRadius: '0.375rem',
                      border: 'none'
                    }}
                  >
                    <h5 className="mb-2" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                      {diagnostico === 'aprovado' ? (
                        <>
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                          </svg>
                          Recomendação: APROVAR
                        </>
                      ) : diagnostico === 'rejeitado-automaticamente' ? (
                        <>
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                          </svg>
                          Recomendação: REJEITAR
                        </>
                      ) : (
                        <>
                          <svg width="24" height="24" viewBox="0 0 16 16" fill="white" className="me-2">
                            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                          </svg>
                          Recomendação: REJEITAR
                        </>
                      )}
                    </h5>
                    <p className="mb-0" style={{ color: 'white', fontSize: '0.85rem' }}>
                      {diagnostico === 'aprovado' 
                        ? 'A solicitação atingiu a pontuação mínima necessária e pode ser aprovada.'
                        : diagnostico === 'rejeitado-automaticamente'
                          ? 'A solicitação foi REJEITADA AUTOMATICAMENTE devido a uma ou mais respostas marcadas como críticas.'
                          : 'A solicitação não atingiu a pontuação mínima necessária. Recomenda-se rejeitar ou solicitar ajustes.'}
                    </p>
                  </div>

                  {/* Alerta de Rejeição Automática */}
                  {rejeitadoAutomaticamente && (
                    <div 
                      className="mb-4" 
                      style={{ 
                        backgroundColor: '#bd2130',
                        color: 'white',
                        padding: '1.25rem',
                        borderRadius: '0.375rem',
                        borderLeft: '4px solid #721c24'
                      }}
                    >
                      <h6 className="mb-2" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="white" className="me-2">
                          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                        </svg>
                        Rejeição automática aplicada!
                      </h6>
                      <p className="mb-2" style={{ color: 'white', fontSize: '0.85rem' }}>
                        Uma ou mais respostas selecionadas foram marcadas como críticas e forçam a rejeição automática da solicitação, 
                        independente da pontuação obtida.
                      </p>
                      {alternativasComRejeitacao.length > 0 && (
                        <div className="mt-3">
                          <strong className="d-block mb-2" style={{ color: 'white', fontSize: '1rem' }}>Respostas críticas identificadas:</strong>
                          <ul className="mb-0" style={{ listStyle: 'none', paddingLeft: '0' }}>
                            {alternativasComRejeitacao.map((item, index) => (
                              <li key={index} className="mb-2 p-2" style={{ 
                                backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                                borderLeft: '3px solid white',
                                borderRadius: '4px',
                                color: 'white',
                                fontSize: '0.75rem'
                              }}>
                                <strong style={{ color: 'white' }}>Pergunta:</strong> {item.pergunta}
                                <br />
                                <strong style={{ color: 'white' }}>Resposta:</strong> {item.alternativa}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recomendações */}
                  {recomendacoes.length > 0 && (
                    <div 
                      className="mb-4" 
                      style={{ 
                        backgroundColor: '#d97706',
                        color: 'white',
                        padding: '1.25rem',
                        borderRadius: '0.375rem',
                        border: 'none'
                      }}
                    >
                      <h6 className="mb-3" style={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="white" className="me-2">
                          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                        </svg>
                        Recomendações e Pontos de Atenção:
                      </h6>
                      <ul className="mb-0" style={{ color: 'white', fontSize: '0.85rem' }}>
                        {recomendacoes.map((rec, index) => (
                          <li key={index}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Resumo das Respostas */}
                  <div className="mt-4">
                    <h6 className="mb-3 d-flex align-items-center">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                        <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                      </svg>
                      Resumo das Respostas
                    </h6>
                    <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' }}>
                      {questionario && questionario.perguntas.map((pergunta: any, index: number) => {
                        const resposta = respostas[pergunta.id];
                        if (!resposta) return null;

                        let respostaTexto = '';
                        if (Array.isArray(resposta)) {
                          respostaTexto = resposta.map(r => {
                            const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(r));
                            return alt?.texto || r;
                          }).join(', ');
                        } else {
                          const alt = pergunta.alternativas.find((a: any) => String(a.valor) === String(resposta));
                          respostaTexto = alt?.texto || resposta;
                        }

                        // Check if this question/alternative triggered automatic rejection
                        const temRejeitacao = alternativasComRejeitacao.some(
                          item => item.pergunta === pergunta.titulo && item.alternativa === respostaTexto
                        );

                        return (
                          <div 
                            key={index} 
                            className="mb-3 pb-3 border-bottom"
                            style={temRejeitacao ? {
                              borderLeft: '4px solid #bd2130',
                              backgroundColor: '#bd2130',
                              paddingLeft: '12px',
                              marginLeft: '-4px',
                              borderRadius: '4px',
                              padding: '12px'
                            } : {}}
                          >
                            <strong className="d-block mb-2" style={temRejeitacao ? { color: 'white' } : {}}>
                              {temRejeitacao && (
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="white" className="me-2">
                                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                                </svg>
                              )}
                              {pergunta.titulo}
                            </strong>
                            <span style={temRejeitacao ? { color: 'white', fontWeight: 500 } : { color: '#6c757d' }}>
                              {respostaTexto}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="cowner-modal-footer">
              {isViewMode ? (
                <>
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={handleExportarPDF}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                      <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                      <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z"/>
                    </svg>
                    Exportar PDF
                  </button>
                  <button 
                    className="btn btn-outline-primary ms-auto" 
                    onClick={() => setShowRegistroModal(false)}
                  >
                    Fechar
                  </button>
                </>
              ) : step === 'dados' ? (
                <>
                  <button className="btn btn-secondary" onClick={() => setShowRegistroModal(false)}>
                    Cancelar
                  </button>
                  <button 
                    className="btn btn-outline-success" 
                    onClick={() => handleSalvarProgresso()}
                    disabled={!dadosSolicitacao.clienteId || !dadosSolicitacao.tipoSolicitacao}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                      <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
                    </svg>
                    Salvar
                  </button>
                  <div className="ms-auto">
                    {tipoSolicitacaoAnterior ? (
                      <button 
                        className="btn btn-primary" 
                        onClick={handleContinuarQuestionario}
                        disabled={!dadosSolicitacao.clienteId || !dadosSolicitacao.tipoSolicitacao}
                      >
                        Continuar Questionário
                      </button>
                    ) : (
                      <button 
                        className="btn btn-outline-primary" 
                        onClick={handleAvancarParaQuestionario}
                        disabled={!dadosSolicitacao.clienteId || !dadosSolicitacao.tipoSolicitacao}
                      >
                        Avançar para Questionário
                      </button>
                    )}
                  </div>
                </>
              ) : step === 'diagnostico' ? (
                <>
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={handleVoltarParaFormulario}
                  >
                    Voltar para Formulário
                  </button>
                  <button 
                    className="btn btn-outline-warning" 
                    onClick={() => {
                      setStep('questionario');
                      // Voltar para a última pergunta respondida
                      if (historicoPerguntasVisitadas.length > 0) {
                        setPerguntaAtual(historicoPerguntasVisitadas[historicoPerguntasVisitadas.length - 1]);
                      }
                    }}
                  >
                    Voltar para Questionário
                  </button>
                  <button 
                    className="btn btn-outline-primary ms-auto" 
                    onClick={() => setShowRegistroModal(false)}
                  >
                    Concluir
                  </button>
                </>
              ) : (
                <>
                  <button 
                    className="btn btn-outline-secondary" 
                    onClick={handleVoltarParaFormulario}
                  >
                    Voltar para Formulário
                  </button>
                  <button 
                    className="btn btn-outline-success" 
                    onClick={() => handleSalvarProgresso()}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                      <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
                    </svg>
                    Salvar
                  </button>
                  <div className="ms-auto d-flex gap-2">
                    <button 
                      className="btn btn-outline-secondary" 
                      onClick={handlePerguntaAnterior}
                      disabled={historicoPerguntasVisitadas.length <= 1}
                    >
                      Anterior
                    </button>
                    <button 
                      className="btn btn-outline-primary" 
                      onClick={handleProximaPergunta}
                    >
                      {perguntaAtual === (questionario?.perguntas.length || 0) - 1 ? 'Finalizar' : 'Próxima'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="cowner-modal-overlay">
          <div 
            className="cowner-modal" 
            style={{ maxWidth: '500px' }}
          >
            <div className="cowner-modal-header">
              <h3>Confirmar Exclusão</h3>
              <button className="cowner-modal-close" onClick={() => setShowDeleteModal(false)} aria-label="Fechar">
                ×
              </button>
            </div>

            <div className="cowner-modal-body">
              <div className="alert alert-warning mb-3">
                <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
                <strong>Atenção!</strong>
              </div>
              <p className="mb-0">
                Tem certeza que deseja deletar esta solicitação? Esta ação não pode ser desfeita.
              </p>
            </div>

            <div className="cowner-modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-danger ms-auto" 
                onClick={confirmDeleteRequest}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                  <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                  <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                </svg>
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Configurar Colunas */}
      {showColumnModal && (
        <div className="cowner-modal-overlay" onClick={() => setShowColumnModal(false)}>
          <div 
            className="cowner-modal" 
            onClick={(e) => e.stopPropagation()} 
            style={{ maxWidth: '600px' }}
          >
            <div className="cowner-modal-header">
              <h3>Configurar Colunas da Tabela</h3>
              <button className="cowner-modal-close" onClick={() => setShowColumnModal(false)} aria-label="Fechar">
                ×
              </button>
            </div>

            <div className="cowner-modal-body">
              <p className="text-muted mb-3">Selecione as colunas que deseja exibir na tabela de solicitações salvas:</p>
              
              <div className="row g-3">
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-cliente"
                      checked={visibleColumns.cliente}
                      onChange={(e) => setVisibleColumns({...visibleColumns, cliente: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-cliente">Cliente</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-tipo"
                      checked={visibleColumns.tipo}
                      onChange={(e) => setVisibleColumns({...visibleColumns, tipo: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-tipo">Tipo</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-status"
                      checked={visibleColumns.status}
                      onChange={(e) => setVisibleColumns({...visibleColumns, status: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-status">Status</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-createdBy"
                      checked={visibleColumns.createdBy}
                      onChange={(e) => setVisibleColumns({...visibleColumns, createdBy: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-createdBy">Criado Por</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-nomeSolicitante"
                      checked={visibleColumns.nomeSolicitante}
                      onChange={(e) => setVisibleColumns({...visibleColumns, nomeSolicitante: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-nomeSolicitante">Nome Solicitante</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-numeroSolicitacao"
                      checked={visibleColumns.numeroSolicitacao}
                      onChange={(e) => setVisibleColumns({...visibleColumns, numeroSolicitacao: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-numeroSolicitacao">Nº Solicitação</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-usuarioId"
                      checked={visibleColumns.usuarioId}
                      onChange={(e) => setVisibleColumns({...visibleColumns, usuarioId: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-usuarioId">ID Usuário</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-nomeUsuario"
                      checked={visibleColumns.nomeUsuario}
                      onChange={(e) => setVisibleColumns({...visibleColumns, nomeUsuario: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-nomeUsuario">Nome Usuário</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-motivoSolicitacao"
                      checked={visibleColumns.motivoSolicitacao}
                      onChange={(e) => setVisibleColumns({...visibleColumns, motivoSolicitacao: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-motivoSolicitacao">Motivo</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-funcaoSAPSolicitada"
                      checked={visibleColumns.funcaoSAPSolicitada}
                      onChange={(e) => setVisibleColumns({...visibleColumns, funcaoSAPSolicitada: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-funcaoSAPSolicitada">Função SAP</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-sistema"
                      checked={visibleColumns.sistema}
                      onChange={(e) => setVisibleColumns({...visibleColumns, sistema: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-sistema">Sistema</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-progresso"
                      checked={visibleColumns.progresso}
                      onChange={(e) => setVisibleColumns({...visibleColumns, progresso: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-progresso">Progresso</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-pontuacaoObtida"
                      checked={visibleColumns.pontuacaoObtida}
                      onChange={(e) => setVisibleColumns({...visibleColumns, pontuacaoObtida: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-pontuacaoObtida">Pontuação</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-diagnostico"
                      checked={visibleColumns.diagnostico}
                      onChange={(e) => setVisibleColumns({...visibleColumns, diagnostico: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-diagnostico">Diagnóstico</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-ultimaAtualizacao"
                      checked={visibleColumns.ultimaAtualizacao}
                      onChange={(e) => setVisibleColumns({...visibleColumns, ultimaAtualizacao: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-ultimaAtualizacao">Última Atualização</label>
                  </div>
                </div>
                
                <div className="col-6">
                  <div className="form-check">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="col-acoes"
                      checked={visibleColumns.acoes}
                      onChange={(e) => setVisibleColumns({...visibleColumns, acoes: e.target.checked})}
                    />
                    <label className="form-check-label" htmlFor="col-acoes">Ações</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="cowner-modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={() => setShowColumnModal(false)}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fullscreen: Monitorar Políticas */}
      {showPoliticasModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ maxWidth: '90vw', width: '90vw', height: '90vh', margin: '5vh auto' }}>
            <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header">
                <h5 className="modal-title">
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                  </svg>
                  Monitorar Políticas de Compliance
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    if (!executingPoliticas) {
                      setShowPoliticasModal(false);
                      setSelectedClientePoliticas('');
                      setPoliticasDisponiveis([]);
                      setSelectedPoliticas([]);
                      setExecucaoResults({});
                    }
                  }}
                  disabled={executingPoliticas}
                  style={{ cursor: executingPoliticas ? 'not-allowed' : 'pointer' }}
                />
              </div>
              
              <div className="modal-body" style={{ overflow: 'hidden', flex: '1', padding: 0, display: 'flex', flexDirection: 'row', height: '100%' }}>
                {/* Coluna Esquerda: Seleção */}
                <div style={{ 
                  width: Object.keys(execucaoResults).length > 0 ? '30%' : '100%',
                  padding: '1rem',
                  overflowY: 'auto',
                  borderRight: Object.keys(execucaoResults).length > 0 ? '1px solid var(--bs-border-color)' : 'none',
                  transition: 'width 0.3s ease',
                  backgroundColor: 'var(--bs-body-bg)'
                }}>
                  {/* Seleção de Cliente */}
                  <div className="mb-4">
                    <label className="form-label d-flex align-items-center mb-2">
                      <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                        <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                      </svg>
                      <strong>Selecione o Cliente</strong>
                    </label>
                    <select
                      className="form-select"
                      value={selectedClientePoliticas}
                      onChange={(e) => handleClientePoliticasChange(e.target.value)}
                      disabled={executingPoliticas}
                    >
                      <option value="">-- Selecione um cliente --</option>
                      {clientesDisponiveis.map((cliente) => (
                        <option key={cliente._id} value={cliente._id}>
                          {cliente.nome}
                        </option>
                      ))}
                    </select>
                    {selectedClientePoliticas && (
                      <small className="text-muted mt-2 d-block">
                        Cliente: <strong>{clientesDisponiveis.find(c => c._id === selectedClientePoliticas)?.nome}</strong>
                      </small>
                    )}
                  </div>

                  {/* Seleção de Sistema */}
                  {selectedClientePoliticas && sistemasDisponiveis.length > 0 && (
                    <div className="mb-4">
                      <label className="form-label d-flex align-items-center mb-2">
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM3.102 4l1.313 7h8.17l1.313-7H3.102zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                        </svg>
                        <strong>Selecione o Sistema SAP</strong>
                      </label>
                      <select
                        className="form-select"
                        value={selectedSistema}
                        onChange={(e) => setSelectedSistema(e.target.value)}
                        disabled={executingPoliticas}
                      >
                        <option value="">-- Selecione um sistema --</option>
                        {sistemasDisponiveis.map((sistema, idx) => (
                          <option key={idx} value={sistema.nome}>
                            {sistema.nome}{sistema.descricao ? ` - ${sistema.descricao}` : ''}
                          </option>
                        ))}
                      </select>
                      {selectedSistema && (
                        <small className="text-muted mt-2 d-block">
                          Sistema: <strong>{selectedSistema}</strong>
                          {sistemasDisponiveis.find(s => s.nome === selectedSistema)?.integrationId ? (
                            <span className="text-success"> • Conector configurado</span>
                          ) : (
                            <span className="text-danger"> • Conector não configurado</span>
                          )}
                        </small>
                      )}
                    </div>
                  )}

                  {/* Políticas Disponíveis */}
                  {selectedClientePoliticas && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <label className="form-label d-flex align-items-center mb-0">
                          <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                            <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5V2zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1H4z"/>
                          </svg>
                          <strong>Políticas Disponíveis</strong>
                        </label>
                        {selectedPoliticas.length > 0 && (
                          <span className="badge bg-primary">
                            {selectedPoliticas.length} selecionada(s)
                          </span>
                        )}
                      </div>

                      {loadingPoliticas ? (
                        <div className="text-center py-5">
                          <Spinner />
                          <p className="text-muted mt-3">Carregando políticas...</p>
                        </div>
                      ) : politicasDisponiveis.length === 0 ? (
                        <div className="alert alert-info" role="alert">
                          <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                          </svg>
                          Nenhuma política ativa encontrada para este cliente.
                        </div>
                      ) : (
                        <>
                          <div className="mb-3">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => {
                                if (selectedPoliticas.length === politicasDisponiveis.length) {
                                  setSelectedPoliticas([]);
                                } else {
                                  setSelectedPoliticas(politicasDisponiveis.map(p => p._id));
                                }
                              }}
                              disabled={executingPoliticas}
                            >
                              {selectedPoliticas.length === politicasDisponiveis.length 
                                ? '☑ Desmarcar Todas' 
                                : '☐ Selecionar Todas'
                              }
                            </button>
                          </div>

                          <div className="list-group">
                            {politicasDisponiveis.map((politica) => (
                              <div 
                                key={politica._id}
                                className={`list-group-item list-group-item-action ${selectedPoliticas.includes(politica._id) ? 'active' : ''}`}
                                style={{ 
                                  cursor: executingPoliticas ? 'not-allowed' : 'pointer',
                                  opacity: executingPoliticas ? 0.6 : 1
                                }}
                                onClick={() => {
                                  if (executingPoliticas) return;
                                  if (selectedPoliticas.includes(politica._id)) {
                                    setSelectedPoliticas(selectedPoliticas.filter(id => id !== politica._id));
                                  } else {
                                    setSelectedPoliticas([...selectedPoliticas, politica._id]);
                                  }
                                }}
                              >
                                <div className="d-flex align-items-start">
                                  <input
                                    type="checkbox"
                                    className="form-check-input me-3"
                                    checked={selectedPoliticas.includes(politica._id)}
                                    onChange={() => {}}
                                    disabled={executingPoliticas}
                                    style={{ cursor: 'pointer', marginTop: '0.25rem' }}
                                  />
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                      <h6 className={`mb-0 fw-bold ${selectedPoliticas.includes(politica._id) ? 'text-white' : ''}`}>
                                        {politica.nome}
                                      </h6>
                                      <span className={`badge ms-2 ${selectedPoliticas.includes(politica._id) ? 'bg-light text-primary' : 'bg-secondary'}`}>
                                        {politica.controles.length} controle(s)
                                      </span>
                                    </div>
                                    {politica.descricao && (
                                      <p className={`small mb-1 ${selectedPoliticas.includes(politica._id) ? 'text-white opacity-90' : 'text-muted'}`}>
                                        {politica.descricao}
                                      </p>
                                    )}
                                    {politica.controles.length > 0 && (
                                      <small className={selectedPoliticas.includes(politica._id) ? 'text-white opacity-75' : 'text-muted'}>
                                        <strong>Controles:</strong> {politica.controles.slice(0, 3).join(', ')}
                                        {politica.controles.length > 3 && ` +${politica.controles.length - 3}`}
                                      </small>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Botão de Executar */}
                  {selectedPoliticas.length > 0 && (
                    <div className="mt-3" style={{ borderTop: '1px solid var(--bs-border-color)', paddingTop: '1rem' }}>
                      <button 
                        type="button" 
                        className="btn btn-success w-100" 
                        onClick={handleExecutarPoliticas}
                        disabled={
                          executingPoliticas || 
                          !selectedSistema || 
                          !sistemasDisponiveis.find(s => s.nome === selectedSistema)?.integrationId
                        }
                        title={
                          !selectedSistema 
                            ? 'Selecione um sistema' 
                            : !sistemasDisponiveis.find(s => s.nome === selectedSistema)?.integrationId 
                            ? 'Sistema sem conector configurado' 
                            : ''
                        }
                      >
                        {executingPoliticas ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Executando...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                              <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
                            </svg>
                            Executar Online/Adhoc ({selectedPoliticas.length})
                          </>
                        )}
                      </button>
                      <button 
                        type="button"
                        className="btn btn-warning flex-grow-1 ms-2"
                        onClick={handleExecutarBackground}
                        disabled={
                          executingBackgroundJob ||
                          executingPoliticas ||
                          selectedPoliticas.length === 0 ||
                          !selectedClientePoliticas ||
                          !selectedSistema ||
                          !sistemasDisponiveis.find(s => s.nome === selectedSistema)?.integrationId
                        }
                        title={
                          selectedPoliticas.length === 0 
                            ? 'Selecione pelo menos uma política' 
                            : !selectedClientePoliticas 
                            ? 'Selecione um cliente' 
                            : !selectedSistema 
                            ? 'Selecione um sistema' 
                            : !sistemasDisponiveis.find(s => s.nome === selectedSistema)?.integrationId 
                            ? 'Sistema sem conector configurado' 
                            : ''
                        }
                      >
                        {executingBackgroundJob ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Agendando...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                              <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                              <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                            </svg>
                            Executar em Background ({selectedPoliticas.length})
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Coluna Direita: Resultados da Execução */}
                {Object.keys(execucaoResults).length > 0 && (
                  <div style={{ 
                    width: '70%',
                    padding: '1rem',
                    overflowY: 'auto',
                    backgroundColor: 'var(--bs-body-bg)'
                  }}>
                    <div className="mb-4">
                      <h5 className="d-flex align-items-center mb-0">
                        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                          <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"/>
                        </svg>
                        <strong>Resultados da Execução</strong>
                      </h5>
                    </div>

                    {Object.entries(execucaoResults).map(([politicaId, execucoes]) => {
                            const politica = politicasDisponiveis.find(p => p._id === politicaId);
                            if (!politica) return null;

                            return (
                              <div key={politicaId} className="mb-4">
                                <h6 className="mb-3">
                                  <span className="badge bg-primary me-2">{politica.nome}</span>
                                </h6>

                                <div className="table-responsive">
                                  <table className="table table-sm table-hover">
                                    <thead>
                                      <tr>
                                        <th>ID</th>
                                        <th>Controle</th>
                                        <th>Status</th>
                                        <th>Violações</th>
                                        <th>Tempo</th>
                                        <th>Resultado</th>
                                        <th>Ações</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {execucoes.map((exec, idx) => (
                                        <tr key={idx} style={{ cursor: exec.status === 'completed' ? 'pointer' : 'default' }}>
                                          <td>
                                            <code className="small">{exec.controlId}</code>
                                          </td>
                                          <td>
                                            <span className="small">{exec.controlName}</span>
                                          </td>
                                          <td>
                                            {exec.status === 'running' && (
                                              <span className="badge bg-warning">
                                                <span className="spinner-border spinner-border-sm me-1" />
                                                Executando...
                                              </span>
                                            )}
                                            {exec.status === 'completed' && (
                                              <span className="badge bg-success">Concluído</span>
                                            )}
                                            {exec.status === 'error' && (
                                              <span className="badge bg-danger" title={exec.error}>Erro</span>
                                            )}
                                            {exec.status === 'pending' && (
                                              <span className="badge bg-secondary">Aguardando</span>
                                            )}
                                          </td>
                                          <td>
                                            {exec.result?.totalViolations !== undefined ? (
                                              <span className={exec.result.totalViolations > 0 ? 'text-danger fw-bold' : 'text-success'}>
                                                {exec.result.totalViolations}
                                              </span>
                                            ) : '-'}
                                          </td>
                                          <td>
                                            {exec.executionTime 
                                              ? `${(exec.executionTime / 1000).toFixed(2)}s` 
                                              : '-'}
                                          </td>
                                          <td>
                                            {exec.result ? (
                                              exec.result.passed ? (
                                                <span className="badge bg-success">✓ Passou</span>
                                              ) : (
                                                <span className="badge bg-danger">Falhou</span>
                                              )
                                            ) : exec.error ? (
                                              <span className="text-danger small">{exec.error}</span>
                                            ) : '-'}
                                          </td>
                                          <td>
                                            {exec.status === 'completed' && exec.result && (
                                              <button
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => handleViewControleDetails(exec)}
                                              >
                                                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                                                  <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                                                  <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                                                </svg>
                                                Detalhes
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            );
                          })}
                  </div>
                )}
              </div>

              <div className="modal-footer" style={{ padding: '1rem 1.5rem', borderTop: '1px solid var(--bs-border-color)' }}>
                {executingPoliticas && (
                  <button 
                    type="button" 
                    className="btn btn-warning me-auto" 
                    onClick={() => {
                      cancelExecutionRef.current = true;
                      abortControllerRef.current?.abort();
                      setAlerta({ tipo: 'warning', mensagem: 'Interrompendo execução...' });
                      setTimeout(() => setAlerta(null), 3000);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                      <path d="M5 3.5h6A1.5 1.5 0 0 1 12.5 5v6a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 11V5A1.5 1.5 0 0 1 5 3.5z"/>
                    </svg>
                    Parar Execução
                  </button>
                )}
                {!executingPoliticas && Object.keys(execucaoResults).length > 0 && (
                  <button 
                    type="button" 
                    className="btn btn-primary me-auto" 
                    onClick={handleSalvarExecucao}
                    disabled={savingExecucao}
                  >
                    {savingExecucao ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="M2 1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9.5a1 1 0 0 0-1 1v7.293l2.646-2.647a.5.5 0 0 1 .708.708l-3.5 3.5a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L7.5 9.293V2a2 2 0 0 1 2-2H14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H2z"/>
                        </svg>
                        Salvar Execução
                      </>
                    )}
                  </button>
                )}
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    if (!executingPoliticas) {
                      setShowPoliticasModal(false);
                      setSelectedClientePoliticas('');
                      setPoliticasDisponiveis([]);
                      setSelectedPoliticas([]);
                      setExecucaoResults({});
                    }
                  }}
                  disabled={executingPoliticas}
                  title={executingPoliticas ? 'Aguarde a conclusão ou pare a execução' : ''}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Controle */}
      {showControleDetailsModal && selectedControleExecution && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1070 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" style={{ zIndex: 1070 }}>
            <div className="modal-content" style={{ maxHeight: '90vh', zIndex: 1070 }}>
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    <code className="text-primary">{selectedControleExecution.controlId}</code>
                    <span className="text-muted mx-2">|</span>
                    <span>{selectedControleExecution.controlName}</span>
                  </h5>
                  {selectedControleExecution.controlDescription && (
                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>{selectedControleExecution.controlDescription}</p>
                  )}
                  {selectedControleExecution.executedAt && (
                    <div className="mt-2">
                      <small className="text-muted">
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                          <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                        </svg>
                        Executado em: {new Date(selectedControleExecution.executedAt).toLocaleString('pt-BR')}
                      </small>
                    </div>
                  )}
                </div>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowControleDetailsModal(false)}
                />
              </div>

              <div className="modal-body">
                {/* Informações do Controle */}
                <div className="mb-3 pb-3 border-bottom">
                  <div className="row g-2 mb-2">
                    {selectedControleExecution.controlCategory && (
                      <div className="col-auto">
                        <small className="text-muted">Categoria:</small>
                        <span className="badge bg-light text-dark border ms-1">
                          {selectedControleExecution.controlCategory === 'authorization' && 'Autorização'}
                          {selectedControleExecution.controlCategory === 'access' && 'Acesso'}
                          {selectedControleExecution.controlCategory === 'segregation' && 'Segregação de Funções'}
                          {selectedControleExecution.controlCategory === 'audit' && 'Auditoria'}
                          {selectedControleExecution.controlCategory === 'security' && 'Segurança'}
                          {selectedControleExecution.controlCategory === 'compliance' && 'Compliance'}
                        </span>
                      </div>
                    )}
                    {selectedControleExecution.controlSeverity && (
                      <div className="col-auto">
                        <small className="text-muted">Severidade:</small>
                        <span className={`badge ms-1 ${getSeverityBadgeClass(selectedControleExecution.controlSeverity)}`}>
                          {getSeverityLabel(selectedControleExecution.controlSeverity)}
                        </span>
                      </div>
                    )}
                    {selectedControleExecution.controlFrameworks && selectedControleExecution.controlFrameworks.length > 0 && (
                      <div className="col-auto">
                        <small className="text-muted">Frameworks:</small>
                        {selectedControleExecution.controlFrameworks.map((fw, idx) => (
                          <span key={idx} className="badge bg-light text-dark border ms-1">{fw}</span>
                        ))}
                      </div>
                    )}
                    {selectedControleExecution.systemName && (
                      <div className="col-auto">
                        <small className="text-muted">Sistema:</small>
                        <span className="badge bg-info text-dark ms-1">
                          {selectedControleExecution.systemName}
                        </span>
                      </div>
                    )}
                    {selectedControleExecution.integrationName && (
                      <div className="col-auto">
                        <small className="text-muted">Conector:</small>
                        <span className="badge bg-success text-dark ms-1">
                          {selectedControleExecution.integrationName}
                        </span>
                      </div>
                    )}
                  </div>
                  {selectedControleExecution.controlRisk && (
                    <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--bs-warning-bg-subtle)', border: '1px solid var(--bs-warning-border-subtle)' }}>
                      <small className="text-muted fw-semibold">Risco:</small>
                      <p className="mb-0 mt-1" style={{ fontSize: '0.85rem', color: 'var(--bs-body-color)' }}>{selectedControleExecution.controlRisk}</p>
                    </div>
                  )}
                </div>

                {selectedControleExecution.controlTechnicalDetails && (
                  <div className="mb-3">
                    <small className="text-muted fw-semibold">Detalhes Técnicos:</small>
                    <p className="mb-0 mt-1" style={{ fontSize: '0.8rem', color: 'var(--bs-body-color)' }}>{selectedControleExecution.controlTechnicalDetails}</p>
                  </div>
                )}

                {/* Alert com Resultado */}
                <div className="mb-3 p-3 border rounded" style={{ 
                  backgroundColor: selectedControleExecution.result?.passed ? 'var(--bs-success-bg-subtle)' : 'var(--bs-danger-bg-subtle)',
                  borderColor: selectedControleExecution.result?.passed ? 'var(--bs-success-border-subtle)' : 'var(--bs-danger-border-subtle)'
                }}>
                  <div className="d-flex align-items-center mb-2">
                    <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-3">
                      {selectedControleExecution.result?.passed ? (
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                      ) : (
                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                      )}
                    </svg>
                    <div>
                      <h6 className="mb-0 fw-bold">
                        {selectedControleExecution.result?.passed ? 'Controle Passou ✓' : 'Controle Falhou'}
                      </h6>
                      <small>
                        Tempo de execução: {selectedControleExecution.executionTime}ms
                      </small>
                    </div>
                  </div>
                  {selectedControleExecution.result?.summary && (
                    <p className="mb-0 mt-2" style={{ color: 'var(--bs-body-color)' }}>{selectedControleExecution.result.summary}</p>
                  )}
                </div>

                {/* Estatísticas */}
                <div className="row g-3 mb-4">
                  <div className="col-md-12">
                    <div className="card border">
                      <div className="card-body text-center py-3">
                        <h3 className="mb-0 text-danger">{selectedControleExecution.result?.totalViolations || 0}</h3>
                        <small className="text-muted">Violações Encontradas</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Achados */}
                {selectedControleExecution.result?.findings && selectedControleExecution.result.findings.length > 0 && (
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">
                      Achados ({selectedControleExecution.result.findings.length})
                    </h6>
                    
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '5%' }}>#</th>
                            <th style={{ width: '10%' }}>Severidade</th>
                            <th style={{ width: '35%' }}>Descrição</th>
                            <th style={{ width: '50%' }}>Detalhes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedControleExecution.result.findings
                            .slice((currentDetailPage - 1) * itemsPerDetailPage, currentDetailPage * itemsPerDetailPage)
                            .map((finding, idx) => {
                              const actualIndex = (currentDetailPage - 1) * itemsPerDetailPage + idx + 1;
                              return (
                                <tr key={idx}>
                                  <td className="align-middle">{actualIndex}</td>
                                  <td className="align-middle">
                                    <span className={getSeverityBadgeClass(finding.severity)}>
                                      {getSeverityLabel(finding.severity)}
                                    </span>
                                  </td>
                                  <td className="align-middle">{finding.description}</td>
                                  <td className="align-middle">
                                    {Object.keys(finding.data).length > 0 && (
                                      <div className="text-muted small">
                                        {Object.entries(finding.data).map(([key, value]) => (
                                          <div key={key}>
                                            <strong>{key}:</strong> {JSON.stringify(value)}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {selectedControleExecution.result.findings.length > itemsPerDetailPage && (() => {
                      const totalPages = Math.ceil(selectedControleExecution.result.findings.length / itemsPerDetailPage);
                      const maxPagesToShow = 5;
                      let startPage = Math.max(1, currentDetailPage - Math.floor(maxPagesToShow / 2));
                      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                      
                      if (endPage - startPage < maxPagesToShow - 1) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }
                      
                      const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
                      
                      return (
                        <nav>
                          <ul className="pagination pagination-sm justify-content-center">
                            <li className={`page-item ${currentDetailPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentDetailPage(1)} disabled={currentDetailPage === 1}>
                                Primeira
                              </button>
                            </li>
                            <li className={`page-item ${currentDetailPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentDetailPage(currentDetailPage - 1)} disabled={currentDetailPage === 1}>
                                Anterior
                              </button>
                            </li>
                            {pages.map(page => (
                              <li key={page} className={`page-item ${currentDetailPage === page ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => setCurrentDetailPage(page)}>
                                  {page}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${currentDetailPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentDetailPage(currentDetailPage + 1)} disabled={currentDetailPage === totalPages}>
                                Próxima
                              </button>
                            </li>
                            <li className={`page-item ${currentDetailPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentDetailPage(totalPages)} disabled={currentDetailPage === totalPages}>
                                Última
                              </button>
                            </li>
                          </ul>
                          <div className="text-center text-muted small">
                            Página {currentDetailPage} de {totalPages} | Total: {selectedControleExecution.result.findings.length} achados
                          </div>
                        </nav>
                      );
                    })()}
                  </div>
                )}

                {/* Recomendações */}
                {selectedControleExecution.result?.recommendations && selectedControleExecution.result.recommendations.length > 0 && (
                  <div className="mb-3">
                    <h6 className="fw-bold mb-3">Recomendações</h6>
                    <ul className="list-group">
                      {selectedControleExecution.result.recommendations.map((rec, idx) => (
                        <li key={idx} className="list-group-item">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="text-info me-2">
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                            <path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286zm1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94z"/>
                          </svg>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowControleDetailsModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Monitorar Resultados Históricos */}
      {showResultadosModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog" style={{ maxWidth: '90vw', width: '90vw', height: '90vh', margin: '5vh auto' }}>
            <div className="modal-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header d-flex justify-content-between align-items-center" style={{ backgroundColor: 'var(--bs-primary)', color: 'white', padding: '1.5rem' }}>
                <h5 className="modal-title d-flex align-items-center mb-0">
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="currentColor" className="me-3">
                    <path d="M1 2.5A1.5 1.5 0 0 1 2.5 1h3A1.5 1.5 0 0 1 7 2.5v3A1.5 1.5 0 0 1 5.5 7h-3A1.5 1.5 0 0 1 1 5.5v-3zM2.5 2a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 1h3A1.5 1.5 0 0 1 15 2.5v3A1.5 1.5 0 0 1 13.5 7h-3A1.5 1.5 0 0 1 9 5.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zM1 10.5A1.5 1.5 0 0 1 2.5 9h3A1.5 1.5 0 0 1 7 10.5v3A1.5 1.5 0 0 1 5.5 15h-3A1.5 1.5 0 0 1 1 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3zm6.5.5A1.5 1.5 0 0 1 10.5 9h3a1.5 1.5 0 0 1 1.5 1.5v3a1.5 1.5 0 0 1-1.5 1.5h-3A1.5 1.5 0 0 1 9 13.5v-3zm1.5-.5a.5.5 0 0 0-.5.5v3a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5v-3a.5.5 0 0 0-.5-.5h-3z"/>
                  </svg>
                  Monitorar Execuções
                </h5>
                <div className="d-flex align-items-center gap-2 ms-auto">
                  {selectedClientePoliticas && (
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => loadExecucoesHistoricas(selectedClientePoliticas)}
                      disabled={loadingExecucoesHistoricas}
                      title="Atualizar lista"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className={loadingExecucoesHistoricas ? 'spinner-border spinner-border-sm' : ''}>
                        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                      </svg>
                      {!loadingExecucoesHistoricas && <span className="ms-1">Atualizar</span>}
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowResultadosModal(false);
                      setSelectedClientePoliticas('');
                      setExecucoesHistoricas([]);
                    }}
                  />
                </div>
              </div>

              <div className="modal-body" style={{ padding: '2rem', overflowY: 'auto' }}>
                {/* Seleção de Cliente */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                      <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H3zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
                    </svg>
                    Selecione o Cliente
                  </label>
                  <select 
                    className="form-select" 
                    value={selectedClientePoliticas}
                    onChange={(e) => handleClienteResultadosChange(e.target.value)}
                  >
                    <option value="">-- Selecione um cliente --</option>
                    {clientesDisponiveis.map(cliente => (
                      <option key={cliente._id} value={cliente._id}>
                        {cliente.nome}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Lista de Execuções Históricas */}
                {selectedClientePoliticas && (
                  <div>
                    <h5 className="mb-3">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                        <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                        <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                      </svg>
                      Histórico de Execuções
                    </h5>

                    {loadingExecucoesHistoricas ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Carregando...</span>
                        </div>
                        <p className="text-muted mt-3">Carregando histórico...</p>
                      </div>
                    ) : execucoesHistoricas.length === 0 ? (
                      <div className="alert alert-info">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                        </svg>
                        Nenhuma execução encontrada para este cliente.
                      </div>
                    ) : (
                      <>
                        {/* Barra de Pesquisa */}
                        <div className="mb-3">
                          <div className="input-group">
                            <span className="input-group-text">
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                              </svg>
                            </span>
                            <input 
                              type="text" 
                              className="form-control" 
                              placeholder="Buscar por política, sistema, gerador..."
                              value={historicoSearch}
                              onChange={(e) => {
                                setHistoricoSearch(e.target.value);
                                setCurrentHistoricoPage(1);
                              }}
                            />
                          </div>
                        </div>
                        
                        <div className="table-responsive">
                          <table className="table table-hover" style={{ fontSize: '0.875rem' }}>
                            <style>{`
                              .table > :not(caption) > * > * { padding: 0.5rem 0.5rem; }
                            `}</style>
                            <thead>
                              <tr>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('executadoEm')}>
                                  Data/Hora {renderHistoricoSortIcon('executadoEm')}
                                </th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('politicaNome')}>
                                  Política {renderHistoricoSortIcon('politicaNome')}
                                </th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('tipo')}>
                                  Tipo {renderHistoricoSortIcon('tipo')}
                                </th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('sistemaNome')}>
                                  Sistema {renderHistoricoSortIcon('sistemaNome')}
                                </th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('status')}>
                                  Status {renderHistoricoSortIcon('status')}
                                </th>
                                <th>Progresso</th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('controles')}>
                                  Controles {renderHistoricoSortIcon('controles')}
                                </th>
                                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleHistoricoSort('violacoes')}>
                                  Violações {renderHistoricoSortIcon('violacoes')}
                                </th>
                                <th>Resultado</th>
                                <th>Gerador por</th>
                                <th>Ações</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                // Filtrar
                                let filtered = execucoesHistoricas.filter((exec: any) => {
                                  if (!historicoSearch) return true;
                                  const searchLower = historicoSearch.toLowerCase();
                                  return (
                                    exec.politicaNome?.toLowerCase().includes(searchLower) ||
                                    exec.sistemaNome?.toLowerCase().includes(searchLower) ||
                                    exec.executadoPor?.username?.toLowerCase().includes(searchLower) ||
                                    exec.executadoPor?.fullName?.toLowerCase().includes(searchLower)
                                  );
                                });
                                
                                // Ordenar
                                if (historicoSortField) {
                                  filtered = [...filtered].sort((a: any, b: any) => {
                                    let aVal, bVal;
                                    
                                    switch (historicoSortField) {
                                      case 'executadoEm':
                                        aVal = new Date(a.executadoEm).getTime();
                                        bVal = new Date(b.executadoEm).getTime();
                                        break;
                                      case 'politicaNome':
                                        aVal = a.politicaNome || '';
                                        bVal = b.politicaNome || '';
                                        break;
                                      case 'tipo':
                                        aVal = a.tipo || '';
                                        bVal = b.tipo || '';
                                        break;
                                      case 'sistemaNome':
                                        aVal = a.sistemaNome || '';
                                        bVal = b.sistemaNome || '';
                                        break;
                                      case 'status':
                                        aVal = a.isJob ? a.jobStatus : 'completed';
                                        bVal = b.isJob ? b.jobStatus : 'completed';
                                        break;
                                      case 'controles':
                                        aVal = a.resumo?.controlesExecutados || 0;
                                        bVal = b.resumo?.controlesExecutados || 0;
                                        break;
                                      case 'violacoes':
                                        aVal = a.resumo?.totalViolacoes || 0;
                                        bVal = b.resumo?.totalViolacoes || 0;
                                        break;
                                      default:
                                        return 0;
                                    }
                                    
                                    if (typeof aVal === 'string') {
                                      return historicoSortOrder === 'asc' 
                                        ? aVal.localeCompare(bVal)
                                        : bVal.localeCompare(aVal);
                                    } else {
                                      return historicoSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                                    }
                                  });
                                }
                                
                                return filtered
                                  .slice((currentHistoricoPage - 1) * itemsPerHistoricoPage, currentHistoricoPage * itemsPerHistoricoPage)
                                  .map((exec: any) => {
                              const isJob = exec.isJob;
                              const isRunning = isJob && (exec.jobStatus === 'running' || exec.jobStatus === 'pending');
                              const isCompleted = isJob && exec.jobStatus === 'completed';
                              return (
                                <tr key={exec._id}>
                                  <td>
                                    <small>{new Date(exec.executadoEm).toLocaleString('pt-BR')}</small>
                                  </td>
                                  <td>
                                    <strong>{exec.politicaNome}</strong>
                                  </td>
                                  <td>
                                    <span className={`badge ${exec.tipo === 'adhoc' ? 'bg-primary' : 'bg-info'}`}>
                                      {exec.tipo === 'adhoc' ? 'Ad-hoc' : 'Job'}
                                    </span>
                                  </td>
                                  <td>
                                    <small>{exec.sistemaNome || '-'}</small>
                                  </td>
                                  <td>
                                    {isJob ? (
                                      <span className={`badge ${
                                        exec.jobStatus === 'pending' ? 'bg-warning' :
                                        exec.jobStatus === 'running' ? 'bg-primary' :
                                        exec.jobStatus === 'completed' ? 'bg-success' :
                                        exec.jobStatus === 'failed' ? 'bg-danger' :
                                        'bg-secondary'
                                      }`}>
                                        {exec.jobStatus === 'pending' ? 'Pendente' :
                                         exec.jobStatus === 'running' ? 'Em execução' :
                                         exec.jobStatus === 'completed' ? 'Concluído' :
                                         exec.jobStatus === 'failed' ? 'Falhou' :
                                         exec.jobStatus}
                                      </span>
                                    ) : (
                                      <span className="badge bg-success">Concluído</span>
                                    )}
                                  </td>
                                  <td>
                                    {isRunning ? (
                                      <div>
                                        <div className="progress" style={{ height: '20px', width: '100px', backgroundColor: '#e9ecef' }}>
                                          <div 
                                            className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                                            role="progressbar" 
                                            style={{ width: `${exec.progresso}%`, fontWeight: '500' }}
                                            aria-valuenow={exec.progresso} 
                                            aria-valuemin={0} 
                                            aria-valuemax={100}
                                          >
                                            {exec.progresso}%
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    {exec.resumo ? (
                                      <span className="badge bg-secondary">
                                        {exec.resumo.controlesExecutados}/{exec.resumo.totalControles}
                                      </span>
                                    ) : <span className="text-muted">-</span>}
                                  </td>
                                  <td>
                                    {exec.resumo && exec.resumo.totalViolacoes !== undefined ? (
                                      <span className={`badge ${exec.resumo.totalViolacoes > 0 ? 'bg-danger' : 'bg-success'}`}>
                                        {exec.resumo.totalViolacoes}
                                      </span>
                                    ) : <span className="text-muted">-</span>}
                                  </td>
                                  <td>
                                    {exec.resumo ? (
                                      exec.resumo.passou ? (
                                        <span className="badge bg-success">✓ Passou</span>
                                      ) : (
                                        <span className="badge bg-danger">✗ Falhou</span>
                                      )
                                    ) : <span className="text-muted">-</span>}
                                  </td>
                                  <td>
                                    <small className="text-muted">
                                      {exec.executadoPor 
                                        ? (typeof exec.executadoPor === 'object' 
                                            ? (exec.executadoPor.fullName || exec.executadoPor.username || 'Sistema')
                                            : 'Sistema')
                                        : 'Sistema'}
                                    </small>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm" role="group">
                                      {(isCompleted || !isJob) && (
                                        <button
                                          className="btn btn-outline-primary"
                                          onClick={() => handleViewExecucaoHistorica(isJob && exec.execucaoId ? exec.execucaoId : exec._id)}
                                          title="Ver Detalhes"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                                            <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                                          </svg>
                                        </button>
                                      )}
                                      {isRunning && (
                                        <button
                                          className="btn btn-outline-warning"
                                          onClick={() => handleCancelJob(exec._id)}
                                          title="Cancelar Job"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                                          </svg>
                                        </button>
                                      )}
                                      {!isRunning && (
                                        <button
                                          className="btn btn-outline-danger"
                                          onClick={() => handleDeleteRecord(exec)}
                                          title="Excluir"
                                        >
                                          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                          </svg>
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            });})(

)}
                            </tbody>
                          </table>
                          {(() => {
                            const filtered = execucoesHistoricas.filter((exec: any) => {
                              if (!historicoSearch) return true;
                              const searchLower = historicoSearch.toLowerCase();
                              return (
                                exec.politicaNome?.toLowerCase().includes(searchLower) ||
                                exec.sistemaNome?.toLowerCase().includes(searchLower) ||
                                exec.executadoPor?.username?.toLowerCase().includes(searchLower) ||
                                exec.executadoPor?.fullName?.toLowerCase().includes(searchLower)
                              );
                            });
                            
                            if (filtered.length === 0 && historicoSearch) {
                              return (
                                <div className="text-center text-muted py-3">
                                  Nenhuma execução encontrada com o termo "{historicoSearch}"
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        
                        {/* Paginação */}
                        {(() => {
                          const filtered = execucoesHistoricas.filter((exec: any) => {
                            if (!historicoSearch) return true;
                            const searchLower = historicoSearch.toLowerCase();
                            return (
                              exec.politicaNome?.toLowerCase().includes(searchLower) ||
                              exec.sistemaNome?.toLowerCase().includes(searchLower) ||
                              exec.executadoPor?.username?.toLowerCase().includes(searchLower) ||
                              exec.executadoPor?.fullName?.toLowerCase().includes(searchLower)
                            );
                          });
                          return filtered.length > itemsPerHistoricoPage && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                              <div className="text-muted">
                                Mostrando {((currentHistoricoPage - 1) * itemsPerHistoricoPage) + 1} a {Math.min(currentHistoricoPage * itemsPerHistoricoPage, filtered.length)} de {filtered.length} execuções
                              </div>
                              <nav>
                                <ul className="pagination mb-0">
                                  <li className={`page-item ${currentHistoricoPage === 1 ? 'disabled' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setCurrentHistoricoPage(currentHistoricoPage - 1)}
                                      disabled={currentHistoricoPage === 1}
                                    >
                                      Anterior
                                    </button>
                                  </li>
                                  {Array.from({ length: Math.ceil(filtered.length / itemsPerHistoricoPage) }, (_, i) => i + 1).map(page => (
                                    <li key={page} className={`page-item ${currentHistoricoPage === page ? 'active' : ''}`}>
                                      <button 
                                        className="page-link" 
                                        onClick={() => setCurrentHistoricoPage(page)}
                                      >
                                        {page}
                                      </button>
                                    </li>
                                  ))}
                                  <li className={`page-item ${currentHistoricoPage === Math.ceil(filtered.length / itemsPerHistoricoPage) ? 'disabled' : ''}`}>
                                    <button 
                                      className="page-link" 
                                      onClick={() => setCurrentHistoricoPage(currentHistoricoPage + 1)}
                                      disabled={currentHistoricoPage === Math.ceil(filtered.length / itemsPerHistoricoPage)}
                                    >
                                      Próxima
                                    </button>
                                  </li>
                                </ul>
                              </nav>
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowResultadosModal(false);
                    setSelectedClientePoliticas('');
                    setExecucoesHistoricas([]);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes da Execução Histórica */}
      {showExecucaoDetalhesModal && selectedExecucaoHistorica && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: '95vw', width: '95%' }}>
            <div className="modal-content" style={{ maxHeight: '95vh' }}>
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    Detalhes da Execução: {selectedExecucaoHistorica.politicaNome}
                  </h5>
                  <small className="text-muted">
                    Executado em: {new Date(selectedExecucaoHistorica.executadoEm).toLocaleString('pt-BR')}
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2 ms-auto">
                  {/* Botão Exportar com Dropdown */}
                  <div className="position-relative">
                    <button 
                      type="button" 
                      className="btn btn-sm btn-primary"
                      onClick={() => setShowExportDropdown(!showExportDropdown)}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                        <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                        <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
                      </svg>
                      Exportar
                    </button>
                    {showExportDropdown && (
                      <div className="dropdown-menu show position-absolute" style={{ top: '100%', right: 0, marginTop: '0.5rem', zIndex: 1050 }}>
                        <button 
                          className="dropdown-item" 
                          onClick={() => {
                            setShowExportDropdown(false);
                            handleExportExecutive();
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                            <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z"/>
                          </svg>
                          Visão Executiva (PDF)
                        </button>
                        <button 
                          className="dropdown-item" 
                          onClick={() => {
                            setShowExportDropdown(false);
                            handleExportTechnical();
                          }}
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                            <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z"/>
                            <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                          </svg>
                          Visão Técnica (PDF)
                        </button>
                      </div>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => {
                      setShowExecucaoDetalhesModal(false);
                      setSelectedExecucaoHistorica(null);
                      setShowExportDropdown(false);
                    }}
                  />
                </div>
              </div>

              <div className="modal-body">
                {/* Resumo Geral com Indicadores */}
                <div className="row g-3 mb-4">
                  {/* Indicadores */}
                  <div className="col-md-4">
                    <div className="card border h-100">
                      <div className="card-body text-center py-3">
                        <h4 className="mb-0">{selectedExecucaoHistorica.resumo.totalControles || 0}</h4>
                        <small className="text-muted d-block">Total de Controles</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border h-100">
                      <div className="card-body text-center py-3">
                        <h4 className="mb-0 text-success">{selectedExecucaoHistorica.resumo.controlesExecutados}</h4>
                        <small className="text-muted d-block">Controles Executados</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card border h-100">
                      <div className="card-body text-center py-3">
                        <h4 className="mb-0 text-danger">{selectedExecucaoHistorica.resumo.totalViolacoes}</h4>
                        <small className="text-muted d-block">Total de Violações</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gráficos */}
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header">
                        <h6 className="mb-0">Controles com Violações por Severidade</h6>
                      </div>
                      <div className="card-body">
                        {(() => {
                          // Contar controles com violações por severidade
                          const severityCounts: Record<string, number> = {
                            critical: 0,
                            high: 0,
                            medium: 0,
                            low: 0
                          };
                          
                          Object.values(selectedExecucaoHistorica.resultados).forEach((execucoes: any) => {
                            execucoes.forEach((exec: any) => {
                              if (exec.result && exec.result.totalViolations > 0 && exec.controlSeverity) {
                                severityCounts[exec.controlSeverity]++;
                              }
                            });
                          });
                          
                          const total = Object.values(severityCounts).reduce((sum, count) => sum + count, 0);
                          
                          const severityData = [
                            { label: 'Crítica', value: severityCounts.critical, color: '#dc3545' },
                            { label: 'Alta', value: severityCounts.high, color: '#fd7e14' },
                            { label: 'Média', value: severityCounts.medium, color: '#ffc107' },
                            { label: 'Baixa', value: severityCounts.low, color: '#0dcaf0' }
                          ].filter(s => s.value > 0);
                          
                          return total > 0 ? (
                            <div>
                              {/* Gráfico Pizza SVG */}
                              <div className="d-flex justify-content-center mb-3">
                                <svg width="200" height="200" viewBox="0 0 200 200">
                                  {(() => {
                                    let currentAngle = 0;
                                    return severityData.map((item, idx) => {
                                      const percentage = item.value / total;
                                      const angle = percentage * 360;
                                      const startAngle = currentAngle;
                                      const endAngle = currentAngle + angle;
                                      currentAngle = endAngle;
                                      
                                      const startRad = (startAngle - 90) * Math.PI / 180;
                                      const endRad = (endAngle - 90) * Math.PI / 180;
                                      
                                      const x1 = 100 + 80 * Math.cos(startRad);
                                      const y1 = 100 + 80 * Math.sin(startRad);
                                      const x2 = 100 + 80 * Math.cos(endRad);
                                      const y2 = 100 + 80 * Math.sin(endRad);
                                      
                                      const largeArc = angle > 180 ? 1 : 0;
                                      
                                      const path = `M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArc} 1 ${x2} ${y2} Z`;
                                      
                                      return (
                                        <path 
                                          key={idx}
                                          d={path}
                                          fill={item.color}
                                        />
                                      );
                                    });
                                  })()}
                                </svg>
                              </div>
                              
                              {/* Legenda */}
                              <div className="mt-3">
                                {severityData.map((item, idx) => (
                                  <div key={idx} className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="d-flex align-items-center">
                                      <div 
                                        style={{ 
                                          width: '16px', 
                                          height: '16px', 
                                          backgroundColor: item.color,
                                          marginRight: '8px',
                                          borderRadius: '2px'
                                        }}
                                      />
                                      <span>{item.label}</span>
                                    </div>
                                    <div>
                                      <strong>{item.value}</strong>
                                      <span className="text-muted ms-2">
                                        ({Math.round((item.value/total)*100)}%)
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-success py-4">
                              <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="mb-2">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                              </svg>
                              <p className="mb-0">Nenhuma violação encontrada</p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card border">
                      <div className="card-header">
                        <h6 className="mb-0">Violações por Controle (Top 5)</h6>
                      </div>
                      <div className="card-body">
                        {(() => {
                          const violacoesPorControle: Array<{name: string, violations: number}> = [];
                          Object.values(selectedExecucaoHistorica.resultados).forEach((execucoes: any) => {
                            execucoes.forEach((exec: any) => {
                              if (exec.result && exec.result.totalViolations > 0) {
                                violacoesPorControle.push({
                                  name: exec.controlName || exec.controlId,
                                  violations: exec.result.totalViolations
                                });
                              }
                            });
                          });
                          
                          const top5 = violacoesPorControle
                            .sort((a, b) => b.violations - a.violations)
                            .slice(0, 5);
                          
                          const maxViolations = top5[0]?.violations || 1;
                          
                          return top5.length > 0 ? (
                            <div>
                              {top5.map((item, idx) => (
                                <div key={idx} className="mb-3">
                                  <div className="d-flex justify-content-between mb-1">
                                    <small className="text-truncate" style={{ maxWidth: '70%' }} title={item.name}>
                                      {item.name}
                                    </small>
                                    <small className="text-danger fw-bold">{item.violations}</small>
                                  </div>
                                  <div className="progress" style={{ height: '20px' }}>
                                    <div 
                                      className="progress-bar bg-danger" 
                                      style={{ width: `${(item.violations/maxViolations)*100}%` }}
                                    >
                                      {item.violations}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center text-success py-4">
                              <svg width="48" height="48" viewBox="0 0 16 16" fill="currentColor" className="mb-2">
                                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                              </svg>
                              <p className="mb-0">Nenhuma violação encontrada</p>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalhes dos Controles */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">Detalhes dos Controles</h6>
                  <div className="input-group" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                      </svg>
                    </span>
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      placeholder="Buscar controles..."
                      value={controleSearch}
                      onChange={(e) => {
                        setControleSearch(e.target.value);
                        setControleCurrentPage(1); // Reset para primeira página ao buscar
                      }}
                    />
                  </div>
                </div>
                {Object.entries(selectedExecucaoHistorica.resultados).map(([politicaId, execucoes]: [string, any]) => {
                  // Filtrar e ordenar controles
                  let filteredControles = execucoes.filter((exec: any) => {
                    if (!controleSearch) return true;
                    const searchLower = controleSearch.toLowerCase();
                    return (
                      exec.controlId?.toLowerCase().includes(searchLower) ||
                      exec.controlName?.toLowerCase().includes(searchLower)
                    );
                  });
                  
                  // Ordenar
                  if (controleSortField) {
                    filteredControles = [...filteredControles].sort((a: any, b: any) => {
                      let aVal, bVal;
                      
                      switch (controleSortField) {
                        case 'id':
                          aVal = a.controlId || '';
                          bVal = b.controlId || '';
                          break;
                        case 'name':
                          aVal = a.controlName || '';
                          bVal = b.controlName || '';
                          break;
                        case 'severity':
                          // Ordem de prioridade: critical > high > medium > low
                          const severityOrder: Record<string, number> = {
                            critical: 4,
                            high: 3,
                            medium: 2,
                            low: 1
                          };
                          aVal = severityOrder[a.controlSeverity] || 0;
                          bVal = severityOrder[b.controlSeverity] || 0;
                          break;
                        case 'status':
                          aVal = a.status || '';
                          bVal = b.status || '';
                          break;
                        case 'violations':
                          aVal = a.result?.totalViolations || 0;
                          bVal = b.result?.totalViolations || 0;
                          break;
                        case 'time':
                          aVal = a.executionTime || 0;
                          bVal = b.executionTime || 0;
                          break;
                        default:
                          return 0;
                      }
                      
                      if (typeof aVal === 'string') {
                        return controleSortOrder === 'asc' 
                          ? aVal.localeCompare(bVal)
                          : bVal.localeCompare(aVal);
                      } else {
                        return controleSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
                      }
                    });
                  }
                  
                  const handleSort = (field: string) => {
                    if (controleSortField === field) {
                      setControleSortOrder(controleSortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setControleSortField(field);
                      setControleSortOrder('asc');
                    }
                    setControleCurrentPage(1); // Reset para primeira página ao ordenar
                  };
                  
                  const SortIcon = ({ field }: { field: string }) => {
                    if (controleSortField !== field) {
                      return (
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1" style={{ opacity: 0.3 }}>
                          <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                        </svg>
                      );
                    }
                    return controleSortOrder === 'asc' ? (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1">
                        <path d="m7.247 4.86-4.796 5.481c-.566.647-.106 1.659.753 1.659h9.592a1 1 0 0 0 .753-1.659l-4.796-5.48a1 1 0 0 0-1.506 0z"/>
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" className="ms-1">
                        <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z"/>
                      </svg>
                    );
                  };
                  
                  return (
                  <div key={politicaId} className="mb-4">
                    <div className="table-responsive">
                      <table className="table table-hover" style={{ fontSize: '0.875rem' }}>
                        <style>{`
                          .table > :not(caption) > * > * { padding: 0.5rem 0.5rem; }
                        `}</style>
                        <thead>
                          <tr>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('id')}>
                              ID <SortIcon field="id" />
                            </th>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>
                              Controle <SortIcon field="name" />
                            </th>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('severity')}>
                              Severidade <SortIcon field="severity" />
                            </th>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                              Status <SortIcon field="status" />
                            </th>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('violations')}>
                              Violações <SortIcon field="violations" />
                            </th>
                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('time')}>
                              Tempo <SortIcon field="time" />
                            </th>
                            <th>Resultado</th>
                            <th>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredControles
                            .slice((controleCurrentPage - 1) * controleItemsPerPage, controleCurrentPage * controleItemsPerPage)
                            .map((exec: any, idx: number) => (
                            <tr key={idx}>
                              <td>
                                <code className="small">{exec.controlId}</code>
                              </td>
                              <td>
                                <span className="small">{exec.controlName}</span>
                              </td>
                              <td>
                                {exec.controlSeverity ? (
                                  <span className={getSeverityBadgeClass(exec.controlSeverity)}>
                                    {getSeverityLabel(exec.controlSeverity)}
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {exec.status === 'completed' && (
                                  <span className="badge bg-success">Concluído</span>
                                )}
                                {exec.status === 'error' && (
                                  <span className="badge bg-danger">Erro</span>
                                )}
                              </td>
                              <td>
                                {exec.result ? (
                                  <span className={`badge ${exec.result.totalViolations > 0 ? 'bg-danger' : 'bg-success'}`}>
                                    {exec.result.totalViolations}
                                  </span>
                                ) : '-'}
                              </td>
                              <td>
                                <small>{exec.executionTime}ms</small>
                              </td>
                              <td>
                                {exec.status === 'completed' ? (
                                  exec.result?.passed ? (
                                    <span className="badge bg-success">✓ Passou</span>
                                  ) : (
                                    <span className="badge bg-danger">Falhou</span>
                                  )
                                ) : exec.error ? (
                                  <span className="text-danger small">{exec.error}</span>
                                ) : '-'}
                              </td>
                              <td>
                                {exec.status === 'completed' && exec.result && (
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleViewControleDetails(exec)}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                                      <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                                      <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                                    </svg>
                                    Detalhes
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {filteredControles.length === 0 && controleSearch && (
                        <div className="text-center text-muted py-3">
                          Nenhum controle encontrado com o termo "{controleSearch}"
                        </div>
                      )}
                    </div>
                    
                    {/* Paginação dos Controles */}
                    {filteredControles.length > controleItemsPerPage && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Mostrando {((controleCurrentPage - 1) * controleItemsPerPage) + 1} a {Math.min(controleCurrentPage * controleItemsPerPage, filteredControles.length)} de {filteredControles.length} controles
                        </div>
                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${controleCurrentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setControleCurrentPage(controleCurrentPage - 1)}
                                disabled={controleCurrentPage === 1}
                              >
                                Anterior
                              </button>
                            </li>
                            {Array.from({ length: Math.ceil(filteredControles.length / controleItemsPerPage) }, (_, i) => i + 1).map(page => (
                              <li key={page} className={`page-item ${controleCurrentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => setControleCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}
                            <li className={`page-item ${controleCurrentPage === Math.ceil(filteredControles.length / controleItemsPerPage) ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => setControleCurrentPage(controleCurrentPage + 1)}
                                disabled={controleCurrentPage === Math.ceil(filteredControles.length / controleItemsPerPage)}
                              >
                                Próxima
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowExecucaoDetalhesModal(false);
                    setSelectedExecucaoHistorica(null);
                    setControleSearch('');
                    setControleSortField('');
                    setControleSortOrder('asc');
                    setControleCurrentPage(1);
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Cancelamento de Job */}
      {showCancelJobModal && jobToCancel && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  Cancelar Job
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowCancelJobModal(false);
                    setJobToCancel(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <strong>Atenção!</strong> O job será interrompido e marcado como cancelado.
                </div>
                <p className="mb-0">
                  Tem certeza que deseja cancelar este job em execução?
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowCancelJobModal(false);
                    setJobToCancel(null);
                  }}
                >
                  Não
                </button>
                <button 
                  type="button" 
                  className="btn btn-warning" 
                  onClick={confirmCancelJob}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"/>
                  </svg>
                  Sim, Cancelar Job
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirmModal && recordToDelete && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  Confirmar Exclusão
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  style={{ filter: 'brightness(0) invert(1)' }}
                  onClick={() => {
                    setShowDeleteConfirmModal(false);
                    setRecordToDelete(null);
                  }}
                />
              </div>
              <div className="modal-body">
                <div className="alert alert-warning mb-3">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                    <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                  </svg>
                  <strong>Atenção!</strong> Esta ação não pode ser desfeita.
                </div>
                <p className="mb-0">
                  Tem certeza que deseja excluir {recordToDelete.isJob ? 'este job' : 'esta execução'}?
                </p>
                {recordToDelete.politicaNome && (
                  <p className="mb-0 mt-2">
                    <strong>Política:</strong> {recordToDelete.politicaNome}
                  </p>
                )}
                {recordToDelete.executadoEm && (
                  <p className="mb-0">
                    <strong>Data:</strong> {new Date(recordToDelete.executadoEm).toLocaleString('pt-BR')}
                  </p>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowDeleteConfirmModal(false);
                    setRecordToDelete(null);
                  }}
                >
                  Cancelar
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmDeleteRecord}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-1">
                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                  </svg>
                  Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container - Canto Inferior Direito */}
      <div style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1080,
        minWidth: '300px',
        maxWidth: '400px'
      }}>
        {toasts.map(toast => (
          <div 
            key={toast.id}
            className={`alert alert-${toast.type} alert-dismissible fade show mb-2`}
            role="alert"
            style={{
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              animation: 'slideInRight 0.3s ease-out'
            }}
          >
            <div className="d-flex align-items-start">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" className="me-2 flex-shrink-0">
                {toast.type === 'success' && (
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                )}
                {toast.type === 'danger' && (
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                )}
                {toast.type === 'warning' && (
                  <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                )}
                {toast.type === 'info' && (
                  <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"/>
                )}
              </svg>
              <div className="flex-grow-1">{toast.message}</div>
            </div>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              aria-label="Close"
            />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default COwnerCockpit;
